import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";

// Configurare API pentru Cloudflare Workers
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

console.log('[FRONTEND] API URL:', API);

// Auth Context
const AuthContext = React.createContext();

// App Settings Context pentru setări globale (logo, temă)
const AppSettingsContext = React.createContext();

const useAppSettings = () => {
  const context = React.useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};

// App Settings Provider pentru setări globale
const AppSettingsProvider = ({ children }) => {
  const [appSettings, setAppSettings] = useState({
    logo_image: null,
    background_image: null,
    theme_colors: {
      primary: "#1e3a8a",
      secondary: "#dc2626",
      accent: "#1e40af"
    },
    header_background: "linear-gradient(135deg, #1e3a8a 0%, #dc2626 50%, #1e3a8a 100%)"
  });
  const [loading, setLoading] = useState(true);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/app-settings`);
      setAppSettings(response.data);
    } catch (error) {
      console.error('Error fetching app settings:', error);
      // Folosește setările default în caz de eroare
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = () => {
    fetchAppSettings();
  };

  useEffect(() => {
    fetchAppSettings();
  }, []);

  return (
    <AppSettingsContext.Provider value={{ 
      appSettings, 
      loading, 
      refreshSettings 
    }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      
      // Adaugă numele de afișare pentru gradul masonic dacă există
      if (userData.masonic_grade) {
        userData.grade_display_name = getGradeDisplayName(userData.masonic_grade);
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const getGradeDisplayName = (grade) => {
    const gradeNames = {
      'ucenic': 'Ucenic (1°)',
      'tovaras': 'Tovărăș (2°)',
      'maestru': 'Maestru (3°)',
      'maestru_secret': 'Maestru Secret (4°)',
      'maestru_perfect': 'Maestru Perfect (5°)',
      'secretar_intim': 'Secretar Intim (6°)',
      'provost_judecator': 'Provost și Judecător (7°)',
      'intendent_constructii': 'Intendent al Construcțiilor (8°)',
      'maestru_ales_noua': 'Maestru Ales al Celor Nouă (9°)',
      'maestru_ales_cincisprezece': 'Maestru Ales al Celor Cincisprezece (10°)',
      'sublim_cavaler_ales': 'Sublim Cavaler Ales (11°)',
      'mare_maestru_arhitect': 'Mare Maestru Arhitect (12°)',
      'cavaler_coroana_regala': 'Cavaler al Coroanei Regale (13°)',
      'mare_ales_perfect': 'Mare Ales Perfect și Sublim Zidaru (14°)',
      'cavaler_orient': 'Cavaler al Orientului (15°)',
      'print_ierusalim': 'Prinț al Ierusalimului (16°)',
      'cavaler_orient_occident': 'Cavaler al Orientului și Occidentului (17°)',
      'cavaler_trandafir_cruce': 'Cavaler al Trandafirului-Cruce (18°)',
      'mare_pontif': 'Mare Pontif (19°)',
      'maestru_viata': 'Maestru ad Vitam (20°)',
      'noachit_cavaler_prusac': 'Noachit sau Cavaler Prusac (21°)',
      'cavaler_coroana_regala_hacha': 'Cavaler al Coroanei Regale și al Toporu (22°)',
      'sef_tabernacol': 'Șeful Tabernacolului (23°)',
      'print_tabernacol': 'Prințul Tabernacolului (24°)',
      'cavaler_sarpe_bronz': 'Cavaler al Șarpelui de Bronz (25°)',
      'print_indurare': 'Prințul Îndurării (26°)',
      'comandor_templu': 'Comandor al Templului (27°)',
      'cavaler_soare': 'Cavaler al Soarelui (28°)',
      'mare_cavaler_scotia': 'Mare Cavaler al Scoției (29°)',
      'cavaler_kadosh': 'Cavaler Kadosh (30°)',
      'mare_inspector_inquisitor': 'Mare Inspector Inchizitor Comandor (31°)',
      'sublim_print_secret_regal': 'Sublim Prinț al Secretului Regal (32°)',
      'suveran_mare_inspector': 'Suveran Mare Inspector General (33°)'
    };
    return gradeNames[grade] || 'Grad Necunoscut';
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Adaugă numele de afișare pentru gradul masonic dacă există
      if (userData.masonic_grade) {
        userData.grade_display_name = getGradeDisplayName(userData.masonic_grade);
      }
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      // Adaugă numele de afișare pentru gradul masonic dacă există
      if (newUser.masonic_grade) {
        newUser.grade_display_name = getGradeDisplayName(newUser.masonic_grade);
      }
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(newUser);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, getGradeDisplayName }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook pentru notificări audio
const useNotificationSound = () => {
  const [isAudioEnabled, setIsAudioEnabled] = useState(() => {
    const saved = localStorage.getItem('mlnr_audio_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const { user } = useAuth();

  const toggleAudio = () => {
    const newValue = !isAudioEnabled;
    setIsAudioEnabled(newValue);
    localStorage.setItem('mlnr_audio_enabled', JSON.stringify(newValue));
  };

  const playNotificationSound = () => {
    if (!isAudioEnabled) return;
    
    try {
      // Creăm un sunet simplu folosind Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Sunet plăcut: două tonuri
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  const checkForNewNotifications = async () => {
    if (!user || user.role === 'admin') return; // Admins nu au nevoie de notificări audio
    
    try {
      const params = lastCheckTime ? `?last_check=${lastCheckTime}` : '';
      const response = await axios.get(`${API}/notifications/internal/recent${params}`);
      
      if (response.data.new_count > 0) {
        playNotificationSound();
        
        // Afișăm și o notificare browser dacă e permisă
        if (Notification.permission === 'granted') {
          const latestNotification = response.data.notifications[0];
          new Notification('Notificare MLNR 1880', {
            body: `${latestNotification.sender_name}: ${latestNotification.title}`,
            icon: '/mlnr-logo.png',
            tag: 'mlnr-notification'
          });
        }
      }
      
      setLastCheckTime(response.data.current_time);
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  // Polling la fiecare 10 secunde
  React.useEffect(() => {
    if (!user || user.role === 'admin') return;
    
    // Cererem permisiunea pentru notificări browser
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    const interval = setInterval(checkForNewNotifications, 10000);
    
    // Verificare inițială
    checkForNewNotifications();
    
    return () => clearInterval(interval);
  }, [user, isAudioEnabled, lastCheckTime]);

  return {
    isAudioEnabled,
    toggleAudio,
    playNotificationSound
  };
};

// Components
const Header = () => {
  const { user, logout } = useAuth();
  const { appSettings } = useAppSettings();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    
    if (user && user.role !== 'admin') {
      // Fetch unread notifications count pentru membri
      const fetchUnreadCount = async () => {
        try {
          const response = await axios.get(`${API}/notifications/internal/unread-count`);
          setUnreadCount(response.data.unread_count);
        } catch (error) {
          console.error('Error fetching unread count:', error);
        }
      };
      
      fetchUnreadCount();
      // Refresh count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const headerStyle = {
    background: appSettings.header_background
  };

  return (
    <header className="text-white shadow-lg border-b-4 border-red-600" style={headerStyle}>
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center p-1 shadow-lg">
              <img 
                src={appSettings.logo_image ? `data:image/png;base64,${appSettings.logo_image}` : "/mlnr-logo.png"}
                alt="MLNR 1880 - Marea Loja Națională Română"
                className="w-18 h-18 object-contain rounded-full"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-wide">MLNR 1880</h1>
              <p className="text-sm opacity-90 font-medium">Marea Loja Națională Română</p>
              <p className="text-xs opacity-75 italic">Fondată 1880</p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-right">
              <p className="text-sm font-medium">
                {user?.role === 'admin' ? '👑 Maestru Venerat' : `F∴ ${user?.first_name} ${user?.last_name}`}
              </p>
              <p className="text-xs opacity-80">
                {user?.grade_display_name || user?.role}
              </p>
              {/* Indicator notificări necitite pentru membri */}
              {user?.role !== 'admin' && unreadCount > 0 && (
                <div className="flex items-center mt-1">
                  <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                    📢 {unreadCount} notificări noi
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Ieșire
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

const LoginForm = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Autentificare</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Parolă</label>
          <input
            type="password"
            required
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          {loading ? 'Se autentifică...' : 'Autentificare'}
        </button>
      </form>
    </div>
  );
};

const RegisterForm = () => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    birth_date: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form data
    if (!formData.first_name.trim()) {
      setError('Prenumele este obligatoriu');
      setLoading(false);
      return;
    }
    
    if (!formData.last_name.trim()) {
      setError('Numele este obligatoriu');
      setLoading(false);
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Email-ul este obligatoriu');
      setLoading(false);
      return;
    }
    
    if (!formData.password.trim()) {
      setError('Parola este obligatorie');
      setLoading(false);
      return;
    }

    // Log form data for debugging
    console.log('Submitting registration data:', formData);

    const result = await register(formData);
    
    if (!result.success) {
      console.error('Registration error:', result.error);
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
        Înregistrare Candidat MLNR 1880
      </h2>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-800 mb-2">Despre Marea Loja Națională Română</h3>
        <p className="text-blue-700 text-sm">
          MLNR 1880 este cea mai veche obediență masonică din România, fondată în 1880. 
          Prin înregistrarea ca și candidat, exprimați dorința de a face parte din această 
          comunitate tradițională de francmasoni români.
        </p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prenume *</label>
            <input
              type="text"
              required
              name="first_name"
              id="first_name"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Introduceți prenumele"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nume *</label>
            <input
              type="text"
              required
              name="last_name"
              id="last_name"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Introduceți numele"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            required
            name="email"
            id="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="exemple@email.ro"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Parolă *</label>
          <input
            type="password"
            required
            name="password"
            id="password"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Introduceți parola"
            minLength="6"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data nașterii (opțional)</label>
          <input
            type="date"
            name="birth_date"
            id="birth_date"
            value={formData.birth_date}
            onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          {loading ? 'Se înregistrează...' : 'Înregistrare'}
        </button>
        
        <div className="text-sm text-gray-600 text-center">
          * Câmpuri obligatorii
        </div>
      </form>
    </div>
  );
};

const CandidateDashboard = () => {
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    cv_document: '',
    intention_letter: '',
    criminal_record: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/applications/my`);
      setApplication(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching application:', error);
      }
    }
  };

  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadData({
          ...uploadData,
          [field]: e.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const submitApplication = async () => {
    if (!uploadData.cv_document || !uploadData.intention_letter || !uploadData.criminal_record) {
      alert('Vă rugăm să încărcați toate documentele necesare');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/applications`, uploadData);
      alert('Aplicația a fost trimisă cu succes!');
      setShowUploadForm(false);
      fetchApplication();
    } catch (error) {
      alert('Eroare la trimiterea aplicației: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'În așteptare';
      case 'approved': return 'Aprobată';
      case 'rejected': return 'Respinsă';
      default: return status;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Panou Candidat</h2>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <p className="text-blue-800">
            Bine ați venit, {user.first_name}! Ca și candidat la Marea Loja Națională Română MLNR 1880, 
            puteți trimite aplicația dvs. cu documentele necesare pentru a deveni membru al celei mai vechi 
            obediente masonice din România, fondată în 1880.
          </p>
        </div>

        {!application ? (
          <div className="text-center">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Documente necesare pentru înregistrarea în MLNR 1880:</h3>
              <ul className="text-left inline-block space-y-2 text-gray-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Curriculum Vitae (CV) detaliat
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Scrisoare de intenție și motivație pentru aderarea la Francmasonerie
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Cazier judiciar valabil (nu mai vechi de 3 luni)
                </li>
              </ul>
              <div className="mt-4 text-sm text-gray-600 bg-yellow-50 p-3 rounded">
                <p><strong>Despre MLNR 1880:</strong> Marea Loja Națională Română este cea mai veche obediență masonică din România, 
                fondată în 1880. Aplicațiile sunt examinate cu atenție de către Marea Lojă.</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowUploadForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
            >
              Începe aplicația
            </button>
          </div>
        ) : (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Statusul aplicației</h3>
            <div className="flex items-center justify-between">
              <div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                  {getStatusText(application.status)}
                </span>
                <p className="text-gray-600 mt-2">
                  Trimisă la: {new Date(application.created_at).toLocaleDateString('ro-RO')}
                </p>
              </div>
            </div>
            
            {application.admin_notes && (
              <div className="mt-4 p-4 bg-white rounded border">
                <h4 className="font-semibold text-gray-800 mb-2">Note administrator:</h4>
                <p className="text-gray-700">{application.admin_notes}</p>
              </div>
            )}
          </div>
        )}

        {showUploadForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Încărcare documente</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CV (PDF, DOC, DOCX)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload('cv_document', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scrisoare de intenție (PDF, DOC, DOCX)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload('intention_letter', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cazier judiciar (PDF, DOC, DOCX)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload('criminal_record', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowUploadForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Anulează
                </button>
                <button
                  onClick={submitApplication}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  {loading ? 'Se trimite...' : 'Trimite aplicația'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componentă pentru afișarea unui sondaj individual
const SurveyCard = ({ survey, onVote, user }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  const isExpired = survey.end_date && new Date(survey.end_date) < new Date();
  const hasVoted = survey.user_has_voted;
  
  const handleOptionChange = (option) => {
    if (survey.allows_multiple_votes) {
      setSelectedOptions(prev => 
        prev.includes(option) 
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    } else {
      setSelectedOptions([option]);
    }
  };
  
  const handleVote = () => {
    if (selectedOptions.length === 0) {
      alert('Vă rugăm să selectați cel puțin o opțiune.');
      return;
    }
    
    if (window.confirm(`Confirmați votul pentru "${survey.title}"?`)) {
      onVote(survey.id, selectedOptions);
      setSelectedOptions([]);
    }
  };
  
  return (
    <div className="bg-white border rounded-lg p-6 shadow-sm">
      <div className="mb-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-gray-800">{survey.title}</h3>
          <div className="flex items-center space-x-2">
            {hasVoted && (
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                ✅ Ați votat
              </span>
            )}
            {isExpired && (
              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                ⏰ Expirat
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-600 text-sm">{survey.description}</p>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-3">
          {survey.allows_multiple_votes ? 'Selectați una sau mai multe opțiuni:' : 'Selectați o opțiune:'}
        </p>
        
        <div className="space-y-2">
          {survey.options.map((option, index) => (
            <label key={index} className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              hasVoted || isExpired 
                ? 'bg-gray-50 cursor-not-allowed' 
                : 'hover:bg-gray-50'
            }`}>
              <input
                type={survey.allows_multiple_votes ? 'checkbox' : 'radio'}
                name={`survey-${survey.id}`}
                checked={selectedOptions.includes(option)}
                onChange={() => handleOptionChange(option)}
                disabled={hasVoted || isExpired}
                className="mr-3"
              />
              <span className={hasVoted || isExpired ? 'text-gray-500' : 'text-gray-800'}>
                {option}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500 space-x-4">
          <span>📊 {survey.total_votes || 0} voturi</span>
          <span>{survey.is_anonymous ? '🎭 Anonim' : '👤 Cu nume'}</span>
          {survey.end_date && (
            <span>⏰ Până la: {new Date(survey.end_date).toLocaleDateString('ro-RO')}</span>
          )}
        </div>
        
        <div className="flex space-x-2">
          {!hasVoted && !isExpired && (
            <button
              onClick={handleVote}
              disabled={selectedOptions.length === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedOptions.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              🗳️ Votează
            </button>
          )}
          
          {(hasVoted || isExpired) && (
            <button
              onClick={() => setShowResults(!showResults)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              📈 {showResults ? 'Ascunde' : 'Vezi'} Rezultate
            </button>
          )}
        </div>
      </div>

      {showResults && survey.results && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="font-semibold mb-3">📊 Rezultate Sondaj</h4>
          <div className="space-y-3">
            {Object.entries(survey.results).map(([option, count]) => {
              const percentage = survey.total_votes > 0 ? Math.round((count / survey.total_votes) * 100) : 0;
              return (
                <div key={option} className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm">{option}</span>
                    <span className="text-sm text-gray-600">{count} voturi ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all" 
                      style={{width: `${percentage}%`}}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const MemberDashboard = () => {
  const { user } = useAuth();
  const { isAudioEnabled, toggleAudio, playNotificationSound } = useNotificationSound();
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showMemberPasswordChange, setShowMemberPasswordChange] = useState(false);
  const [memberPasswordData, setMemberPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPhoneNumberChange, setShowPhoneNumberChange] = useState(false);
  const [phoneNumberData, setPhoneNumberData] = useState('');
  const [newMessage, setNewMessage] = useState({
    content: '',
    recipients: [],
    message_type: 'direct',
    is_help_request: false
  });

  useEffect(() => {
    fetchMembers();
    fetchMessages();
    fetchNotifications();
    fetchLeadership();
    fetchSurveys();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members/with-photos`);
      setMembers(response.data.filter(member => member.id !== user.id));
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchLeadership = async () => {
    try {
      const response = await axios.get(`${API}/leadership`);
      setLeadership(response.data);
    } catch (error) {
      console.error('Error fetching leadership:', error);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Verifică tipul fișierului
      if (!file.type.startsWith('image/')) {
        alert('Vă rugăm să selectați un fișier imagine valid (JPG, PNG, etc.)');
        return;
      }
      
      // Verifică dimensiunea (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Imaginea este prea mare. Vă rugăm să folosiți o imagine mai mică de 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await axios.put(`${API}/profile/photo`, {
            profile_photo: e.target.result
          });
          alert('Poza de profil a fost actualizată cu succes!');
          setShowPhotoUpload(false);
          // Refresh user data
          window.location.reload();
        } catch (error) {
          alert('Eroare la actualizarea pozei de profil: ' + (error.response?.data?.detail || 'Unknown error'));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const deleteProfilePhoto = async () => {
    if (!window.confirm('Sigur doriți să ștergeți poza de profil?')) {
      return;
    }

    try {
      await axios.delete(`${API}/profile/photo`);
      alert('Poza de profil a fost ștearsă cu succes!');
      window.location.reload();
    } catch (error) {
      alert('Eroare la ștergerea pozei de profil: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      // Fetch birthday notifications
      const birthdayResponse = await axios.get(`${API}/birthdays/notifications`);
      
      // Fetch internal notifications  
      const internalResponse = await axios.get(`${API}/notifications/internal`);
      
      // Combine both types of notifications
      const combinedNotifications = [
        ...birthdayResponse.data.map(notif => ({...notif, type: 'birthday'})),
        ...internalResponse.data.map(notif => ({...notif, type: 'internal'}))
      ];
      
      // Sort by creation date
      combinedNotifications.sort((a, b) => new Date(b.created_at || b.sent_at) - new Date(a.created_at || a.sent_at));
      
      setNotifications(combinedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.content.trim() || newMessage.recipients.length === 0) {
      alert('Vă rugăm să completați mesajul și să selectați destinatarii');
      return;
    }

    try {
      await axios.post(`${API}/messages`, newMessage);
      alert('Mesajul a fost trimis cu succes!');
      setNewMessage({
        content: '',
        recipients: [],
        message_type: 'direct',
        is_help_request: false
      });
      fetchMessages();
    } catch (error) {
      alert('Eroare la trimiterea mesajului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/birthdays/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Funcții pentru sondaje (membri)
  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API}/surveys`);
      setSurveys(response.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const voteInSurvey = async (surveyId, selectedOptions) => {
    try {
      await axios.post(`${API}/surveys/vote`, {
        survey_id: surveyId,
        selected_options: selectedOptions
      });
      alert('Votul a fost înregistrat cu succes!');
      fetchSurveys(); // Refresh pentru a actualiza status-ul de vot
    } catch (error) {
      alert('Eroare la înregistrarea votului: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  // Funcții pentru ștergerea notificărilor și mesajelor (membri)
  const deleteNotificationForMe = async (notificationId) => {
    if (window.confirm('Sigur doriți să ștergeți această notificare din vederea dumneavoastră?')) {
      try {
        await axios.put(`${API}/notifications/internal/${notificationId}/delete-for-me`);
        alert('Notificarea a fost ștearsă din vederea dumneavoastră!');
        fetchNotifications(); // Refresh notificările
      } catch (error) {
        alert('Eroare la ștergerea notificării: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
      }
    }
  };

  const deleteMessageForMe = async (messageId) => {
    if (window.confirm('Sigur doriți să ștergeți acest mesaj din vederea dumneavoastră?')) {
      try {
        await axios.put(`${API}/messages/${messageId}/delete-for-me`);
        alert('Mesajul a fost șters din vederea dumneavoastră!');
        fetchMessages(); // Refresh mesajele
      } catch (error) {
        alert('Eroare la ștergerea mesajului: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
      }
    }
  };

  // Funcție pentru schimbarea propriei parole (membri)
  const changeMemberPassword = async () => {
    if (!memberPasswordData.current_password || !memberPasswordData.new_password || !memberPasswordData.confirm_password) {
      alert('Toate câmpurile sunt obligatorii');
      return;
    }

    if (memberPasswordData.new_password !== memberPasswordData.confirm_password) {
      alert('Parola nouă și confirmarea nu se potrivesc');
      return;
    }

    if (memberPasswordData.new_password.length < 8) {
      alert('Parola nouă trebuie să aibă cel puțin 8 caractere');
      return;
    }

    if (!window.confirm('Sunteți sigur că doriți să vă schimbați parola? Va trebui să vă reconectați cu noua parolă.')) {
      return;
    }

    try {
      const response = await axios.put(`${API}/members/change-password`, memberPasswordData);
      
      alert(
        `✅ ${response.data.message}\n\n` +
        `👤 Utilizator: ${response.data.user_name}\n` +
        `🔐 Rol: ${response.data.user_role === 'member' ? 'Frate' : 'Candidat'}\n\n` +
        `ℹ️ ${response.data.security_note}`
      );
      
      setShowMemberPasswordChange(false);
      setMemberPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      // Opțional: deconectarea automată pentru a forța reconectarea cu noua parolă
      if (window.confirm('Doriți să vă deconectați acum pentru a vă reconecta cu noua parolă?')) {
        window.location.reload();
      }
      
    } catch (error) {
      alert('Eroare la schimbarea parolei: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Funcție pentru actualizarea numărului de telefon
  const updatePhoneNumber = async () => {
    try {
      const response = await axios.put(`${API}/profile/phone`, {
        phone_number: phoneNumberData
      });
      
      alert('✅ ' + response.data.message);
      setShowPhoneNumberChange(false);
      setPhoneNumberData('');
      // Actualizăm profilul utilizatorului
      window.location.reload();
    } catch (error) {
      alert('Eroare la actualizarea numărului de telefon: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Prezentare generală
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'messages'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mesaje
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notifications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notificări
              {notifications.filter(n => !n.is_read).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                  {notifications.filter(n => !n.is_read).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('leadership')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leadership'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Conducerea MLNR 1880
            </button>
            <button
              onClick={() => setActiveTab('surveys')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'surveys'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Sondaje
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚙️ Setări
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👤 Profilul Meu
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">
                Bine ați venit, Frate {user.first_name}!
              </h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Marea Loja Națională Română MLNR 1880</strong> - Cea mai veche obediență masonică din România. 
                  Aici puteți comunica cu frații din Lojă și primi informații importante.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Frați Activi în MLNR 1880</h3>
                  <p className="text-3xl font-bold text-blue-600">{members.length + 1}</p>
                  <p className="text-blue-600 text-sm">membri înregistrați</p>
                </div>
                
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Activitate Recentă</h3>
                  <p className="text-3xl font-bold text-green-600">{messages.length}</p>
                  <p className="text-green-600 text-sm">mesaje între frați</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Frații din MLNR 1880</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members.map((member) => (
                    <div key={member.id} className="bg-white border rounded-lg p-4 shadow-sm">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          {member.profile_photo ? (
                            <img 
                              src={member.profile_photo} 
                              alt={`${member.first_name} ${member.last_name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-gray-500 text-lg font-bold">
                              {member.first_name.charAt(0)}{member.last_name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">F∴ {member.first_name} {member.last_name}</h4>
                          <p className="text-sm text-gray-600 capitalize">
                            {member.role === 'admin' ? 'Maestru Venerat' : 'Frate'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-500 space-y-1">
                        <p><strong>Grad:</strong> {member.grade_display_name}</p>
                        <p><strong>Email:</strong> {member.email}</p>
                        {member.phone_number && member.can_see_phone && (
                          <p><strong>📞 Telefon:</strong> {member.phone_number}</p>
                        )}
                        {member.birth_date && (
                          <p><strong>Născut:</strong> {new Date(member.birth_date).toLocaleDateString('ro-RO')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'leadership' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">👥 Conducerea MLNR 1880</h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Conducerea Marii Loji Naționale Române</strong> - Frații care conduc activitățile 
                  și îndeplinesc funcțiile oficiale în cadrul MLNR 1880.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leadership.map((leader) => (
                  <div key={leader.id} className="bg-white border rounded-lg p-6 shadow-lg">
                    <div className="text-center mb-4">
                      <div className="w-24 h-24 mx-auto rounded-full overflow-hidden bg-gray-200 mb-3">
                        <img 
                          src={leader.photo} 
                          alt={`${leader.first_name} ${leader.last_name}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">{leader.title}</h3>
                      <p className="text-md font-semibold text-blue-600">
                        F∴ {leader.first_name} {leader.last_name}
                      </p>
                      <p className="text-sm text-purple-600 font-medium">
                        {leader.grade_display_name} ({leader.grade_level}°)
                      </p>
                    </div>
                    
                    <div className="border-t pt-3">
                      <p className="text-sm text-gray-700 mb-3">{leader.description}</p>
                      
                      {leader.email && (
                        <p className="text-xs text-gray-600">
                          <strong>Email:</strong> {leader.email}
                        </p>
                      )}
                      {leader.phone && (
                        <p className="text-xs text-gray-600">
                          <strong>Telefon:</strong> {leader.phone}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">⚙️ Setări Personale</h2>
              
              <div className="max-w-4xl">
                {/* Notificări Audio */}
                <div className="bg-white border rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    🔊 Notificări Audio
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">Sunet Notificări</h4>
                        <p className="text-sm text-gray-600">
                          Activează/dezactivează sunetul pentru notificările noi primite de la administrare
                        </p>
                      </div>
                      <div className="flex items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAudioEnabled}
                            onChange={toggleAudio}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {isAudioEnabled ? '🔊 Activat' : '🔇 Dezactivat'}
                        </span>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="mr-3 mt-0.5">
                          <span className="text-blue-500">💡</span>
                        </div>
                        <div>
                          <h5 className="font-medium text-blue-800 mb-1">Despre Notificările Audio</h5>
                          <p className="text-sm text-blue-700">
                            Când primești notificări de la administratori, vei auzi un sunet discret pentru a fi anunțat. 
                            Sunetul funcționează doar când ai aplicația deschisă.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={() => {
                          playNotificationSound();
                          alert('Test sunet notificare executat! Dacă nu ai auzit sunetul, verifică setările audio ale browser-ului.');
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center"
                        disabled={!isAudioEnabled}
                      >
                        🎵 Testează Sunetul
                      </button>
                    </div>
                  </div>
                </div>

                {/* Notificări Browser */}
                <div className="bg-white border rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    🌐 Notificări Browser
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-800">Status Permisiuni</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          Notification.permission === 'granted' 
                            ? 'bg-green-100 text-green-800' 
                            : Notification.permission === 'denied'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {Notification.permission === 'granted' ? '✅ Permise' : 
                           Notification.permission === 'denied' ? '❌ Respinse' : '⏳ În Așteptare'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Notificările browser îți permit să primești alertele MLNR 1880 chiar și atunci când aplicația 
                        nu este în focus.
                      </p>
                      
                      {Notification.permission !== 'granted' && (
                        <button
                          onClick={() => Notification.requestPermission()}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded text-sm transition-colors"
                        >
                          🔔 Activează Notificările Browser
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Setări Interfață */}
                <div className="bg-white border rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    🎨 Preferințe Interfață
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">Dimensiune Font</h4>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="small">Mic</option>
                        <option value="medium" selected>Mediu (Recomandat)</option>
                        <option value="large">Mare</option>
                      </select>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-800 mb-3">Mod Compact</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-3 text-sm text-gray-600">Interfață mai compactă</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Setări Securitate */}
                <div className="bg-white border rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    🔐 Securitate Cont
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">Schimbare Parolă</h4>
                        <p className="text-sm text-gray-600">
                          Schimbați-vă parola pentru a menține contul securizat
                        </p>
                      </div>
                      <button
                        onClick={() => setShowMemberPasswordChange(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                      >
                        🔑 Schimbă Parola
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600 bg-yellow-50 p-2 rounded mt-3">
                    <strong>Recomandare:</strong> Folosiți o parolă cu minimum 8 caractere, conținând litere mari, mici, cifre și simboluri.
                  </div>
                </div>

                {/* Setări Confidențialitate */}
                <div className="bg-white border rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    🔒 Confidențialitate
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">Profil Vizibil</h4>
                        <p className="text-sm text-gray-600">
                          Permite celorlalți membri să vadă poza și informațiile tale de profil
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">Data Nașterii Vizibilă</h4>
                        <p className="text-sm text-gray-600">
                          Afișează data nașterii în profilul tău pentru felicitări
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Setări Activitate */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    📊 Activitate și Statistici
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Statistici Personale</h4>
                      <div className="text-sm text-blue-700 space-y-1">
                        <p>• Sondaje completate: Disponibil în curând</p>
                        <p>• Mesaje trimise: Disponibil în curând</p>
                        <p>• Zile active: Disponibil în curând</p>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">Preferințe Comunicare</h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="ml-2 text-sm text-green-700">Primesc mesaje directe</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="ml-2 text-sm text-green-700">Notificări sondaje noi</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">👤 Profilul Meu - MLNR 1880</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Photo Section */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">📸 Poza de Profil</h3>
                  
                  <div className="text-center mb-4">
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gray-200 border-4 border-blue-200">
                      {user.profile_photo ? (
                        <img 
                          src={user.profile_photo} 
                          alt={`${user.first_name} ${user.last_name}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500 text-2xl font-bold">
                          {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowPhotoUpload(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      {user.profile_photo ? 'Schimbă Poza' : 'Adaugă Poză de Profil'}
                    </button>
                    
                    {user.profile_photo && (
                      <button
                        onClick={deleteProfilePhoto}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                      >
                        Șterge Poza de Profil
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setPhoneNumberData(user.phone_number || '');
                        setShowPhoneNumberChange(true);
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      📞 {user.phone_number ? 'Actualizează Telefon' : 'Adaugă Telefon'}
                    </button>
                  </div>
                  
                  <div className="mt-4 text-xs text-gray-600 bg-yellow-50 p-2 rounded">
                    <strong>Importante:</strong> Folosiți o poză profesională și clară. 
                    Formatul acceptat: JPG, PNG. Dimensiune maximă: 5MB.
                  </div>
                </div>

                {/* Profile Information */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">ℹ️ Informații Profil</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Nume complet:</span>
                      <span className="font-medium">F∴ {user.first_name} {user.last_name}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{user.email}</span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Rol în MLNR 1880:</span>
                      <span className="font-medium text-blue-600 capitalize">
                        {user.role === 'admin' ? 'Maestru Venerat' : 
                         user.role === 'member' ? 'Frate' : 'Candidat'}
                      </span>
                    </div>
                    {user.masonic_grade && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Grad Masonic:</span>
                        <span className="font-medium text-purple-600">{user.grade_display_name}</span>
                      </div>
                    )}
                    {user.birth_date && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Data nașterii:</span>
                        <span className="font-medium">{new Date(user.birth_date).toLocaleDateString('ro-RO')}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Telefon:</span>
                      <span className="font-medium">
                        {user.phone_number ? user.phone_number : 'Nu este setat'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Membru din:</span>
                      <span className="font-medium">{new Date(user.created_at).toLocaleDateString('ro-RO')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-green-600">
                        {user.is_active ? '🟢 Activ' : '🔴 Inactiv'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo Upload Modal */}
              {showPhotoUpload && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold mb-4">📸 Încărcare Poză de Profil</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Selectați o imagine (JPG, PNG - max 5MB)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                      <p className="text-blue-800 text-xs">
                        <strong>Recomandări:</strong> Folosiți o poză clară, profesională, în care 
                        fața este bine vizibilă. Evitați pozele întunecate sau neclare.
                      </p>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowPhotoUpload(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Anulează
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Phone Number Update Modal */}
              {showPhoneNumberChange && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold mb-4">📞 Actualizare Număr de Telefon</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Numărul de telefon (vizibil pentru frați conform gradelor masonice)
                      </label>
                      <input
                        type="tel"
                        value={phoneNumberData}
                        onChange={(e) => setPhoneNumberData(e.target.value)}
                        placeholder="Ex: +40 21 XXX XXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                      <p className="text-blue-800 text-xs">
                        <strong>Vizibilitate:</strong> Numărul dumneavoastră de telefon va fi vizibil 
                        pentru frații din lojă conform ierarhiei gradelor masonice. Frații cu grade 
                        superioare pot vedea numerele fraților cu grade inferioare.
                      </p>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowPhoneNumberChange(false);
                          setPhoneNumberData('');
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Anulează
                      </button>
                      <button
                        onClick={updatePhoneNumber}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md"
                      >
                        Salvează
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Mesaje</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Trimite mesaj</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Destinatari</label>
                      <select
                        multiple
                        value={newMessage.recipients}
                        onChange={(e) => setNewMessage({
                          ...newMessage,
                          recipients: Array.from(e.target.selectedOptions, option => option.value)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        size={4}
                      >
                        {members.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newMessage.is_help_request}
                          onChange={(e) => setNewMessage({
                            ...newMessage,
                            is_help_request: e.target.checked
                          })}
                          className="mr-2"
                        />
                        Cerere de ajutor
                      </label>
                    </div>
                    
                    <div>
                      <textarea
                        value={newMessage.content}
                        onChange={(e) => setNewMessage({
                          ...newMessage,
                          content: e.target.value
                        })}
                        placeholder="Scrieți mesajul..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <button
                      onClick={sendMessage}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Trimite mesaj
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Mesaje recente</h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {messages.map((message) => (
                      <div key={message.id} className="bg-white border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">
                              {message.sender?.first_name} {message.sender?.last_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(message.created_at).toLocaleString('ro-RO')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {message.is_help_request && (
                              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                                Cerere ajutor
                              </span>
                            )}
                            <button
                              onClick={() => deleteMessageForMe(message.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Șterge mesajul din vederea ta"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700">{message.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📬 Notificări MLNR 1880</h2>
              
              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nu aveți notificări.</p>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 rounded-lg border ${
                        (notification.is_read === false || notification.type === 'internal') && !notification.is_read 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {notification.type === 'birthday' ? (
                            <div>
                              <div className="flex items-center mb-2">
                                <span className="text-2xl mr-2">🎉</span>
                                <p className="font-medium text-lg text-green-800">Felicitare Ziua de Naștere</p>
                              </div>
                              <p className="text-gray-700 mb-2">{notification.message}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(notification.sent_at).toLocaleString('ro-RO')}
                              </p>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center mb-2">
                                <span className="text-2xl mr-2">📢</span>
                                <p className="font-medium text-lg text-blue-800">{notification.title}</p>
                                {!notification.is_read && (
                                  <span className="ml-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">NOU</span>
                                )}
                              </div>
                              <p className="text-gray-700 mb-2">{notification.content}</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <p className="text-sm text-gray-500">
                                    {new Date(notification.created_at).toLocaleString('ro-RO')}
                                  </p>
                                  {notification.sender && (
                                    <p className="text-sm text-purple-600">
                                      De la: {notification.sender.name}
                                      {notification.sender.role === 'admin' && ' (Maestru Venerat)'}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  {notification.grade_display_name && (
                                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                                      Grad: {notification.grade_display_name}
                                    </span>
                                  )}
                                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                    Notificare Internă
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {notification.type === 'birthday' && (
                          <div className="flex space-x-2 ml-4">
                            {!notification.is_read && (
                              <button
                                onClick={() => markNotificationRead(notification.id)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                ✓ Marchează ca citit
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (window.confirm('Sigur doriți să ștergeți această felicitare din vederea dumneavoastră?')) {
                                  // Pentru notificările birthday nu avem funcție dedicată, dar putem adăuga una
                                  alert('Funcția de ștergere felicitări va fi implementată în următoarea versiune.');
                                }
                              }}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                              title="Șterge felicitarea din vederea ta"
                            >
                              🗑️ Șterge
                            </button>
                          </div>
                        )}
                        {notification.type === 'internal' && (
                          <div className="flex space-x-2 ml-4">
                            {!notification.is_read && (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/notifications/internal/${notification.id}/read`);
                                    fetchNotifications(); // Refresh notifications
                                  } catch (error) {
                                    console.error('Error marking notification as read:', error);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                ✓ Marchează ca citit
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotificationForMe(notification.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                              title="Șterge notificarea din vederea ta"
                            >
                              🗑️ Șterge
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'surveys' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📊 Sondaje MLNR 1880</h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Sondaje Active</strong> - Participați la sondajele create de administrarea MLNR 1880 pentru a vă exprima opiniile.
                </p>
              </div>

              <div className="space-y-6">
                {surveys.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nu există sondaje active în acest moment.
                  </div>
                ) : (
                  surveys.map((survey) => (
                    <SurveyCard 
                      key={survey.id} 
                      survey={survey} 
                      onVote={voteInSurvey}
                      user={user}
                    />
                  ))
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Member Password Change Modal */}
      {showMemberPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">🔑 Schimbare Parolă</h3>
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-gray-700">
                <strong>Utilizator:</strong> F∴ {user.first_name} {user.last_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Rol:</strong> {user.role === 'member' ? 'Frate' : 'Candidat'}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parola Curentă</label>
                <input
                  type="password"
                  value={memberPasswordData.current_password}
                  onChange={(e) => setMemberPasswordData({
                    ...memberPasswordData, 
                    current_password: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Introduceți parola curentă"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parolă Nouă</label>
                <input
                  type="password"
                  value={memberPasswordData.new_password}
                  onChange={(e) => setMemberPasswordData({
                    ...memberPasswordData, 
                    new_password: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Introduceți parola nouă"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmă Parola Nouă</label>
                <input
                  type="password"
                  value={memberPasswordData.confirm_password}
                  onChange={(e) => setMemberPasswordData({
                    ...memberPasswordData, 
                    confirm_password: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirmați parola nouă"
                />
              </div>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-yellow-800 text-xs">
                <strong>ℹ️ Informație:</strong> După schimbarea parolei, vi se va cere să vă reconectați cu noua parolă 
                pentru a confirma modificarea și a asigura securitatea contului.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMemberPasswordChange(false);
                  setMemberPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={changeMemberPassword}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                🔑 Schimbă Parola
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Photo Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">📸 Încărcare Poză de Profil</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selectează fișierul
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Acceptă: JPG, PNG, GIF. Dimensiune maximă: 5MB
                </p>
              </div>
              
              {/* profilePhoto preview removed - will be handled in component state */}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPhotoUpload(false);
                  setProfilePhoto(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={async () => {
                  if (!profilePhoto) {
                    alert('Vă rugăm să selectați o poză');
                    return;
                  }
                  
                  try {
                    const formData = new FormData();
                    // Convert base64 to blob
                    const response = await fetch(profilePhoto);
                    const blob = await response.blob();
                    formData.append('photo', blob, 'profile.jpg');
                    
                    await axios.post(`${API}/members/profile-photo`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });
                    
                    alert('Poza de profil a fost actualizată cu succes!');
                    setShowPhotoUpload(false);
                    setProfilePhoto(null);
                    window.location.reload(); // Refresh pentru a afișa noua poză
                  } catch (error) {
                    alert('Eroare la încărcarea pozei: ' + (error.response?.data?.detail || 'Unknown error'));
                  }
                }}
                disabled={!profilePhoto}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:bg-gray-400"
              >
                Încarcă Poza
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { getGradeDisplayName } = useAuth();
  const { appSettings: globalAppSettings, refreshSettings } = useAppSettings();
  const [applications, setApplications] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [birthdayMembers, setBirthdayMembers] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [platformStats, setPlatformStats] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [birthdayMessage, setBirthdayMessage] = useState('La mulți ani! Vă urăm sănătate și fericire din partea MLNR 1880!');
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailData, setEmailData] = useState({
    current_email: '',
    new_email: '',
    password: ''
  });
  const [showBackupOptions, setShowBackupOptions] = useState(false);
  const [backupData, setBackupData] = useState(null);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [showMemberPasswordReset, setShowMemberPasswordReset] = useState(false);
  const [selectedMemberForPasswordReset, setSelectedMemberForPasswordReset] = useState(null);
  const [memberPasswordResetData, setMemberPasswordResetData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMemberData, setNewMemberData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    birth_date: '',
    role: 'member',
    masonic_grade: ''
  });
  const [gradeHierarchy, setGradeHierarchy] = useState({});
  const [myAccessibleGrades, setMyAccessibleGrades] = useState({});
  const [gradeInstructions, setGradeInstructions] = useState([]);
  const [gradeNotifications, setGradeNotifications] = useState([]);
  const [showInstructionUpload, setShowInstructionUpload] = useState(false);
  const [showGradeNotificationForm, setShowGradeNotificationForm] = useState(false);
  const [showEditInstruction, setShowEditInstruction] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [newInstruction, setNewInstruction] = useState({
    grade: '',
    title: '',
    description: '',
    pdf_content: ''
  });
  const [newGradeNotification, setNewGradeNotification] = useState({
    title: '',
    content: '',
    target_grades: [],
    notification_type: 'general',
    send_to_all_grades: false
  });
  const [showGradeSelector, setShowGradeSelector] = useState({show: false, member: null});
  const [selectedGrade, setSelectedGrade] = useState('');
  const [showAppSettingsForm, setShowAppSettingsForm] = useState(false);
  const [membersByCategories, setMembersByCategories] = useState({});
  const [selectedMemberProfile, setSelectedMemberProfile] = useState(null);
  const [showMemberProfileModal, setShowMemberProfileModal] = useState(false);
  const [showCVUploadModal, setShowCVUploadModal] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  
  // Stări pentru sondaje
  const [surveys, setSurveys] = useState([]);
  const [showSurveyForm, setShowSurveyForm] = useState(false);
  const [showSurveyResults, setShowSurveyResults] = useState(null);
  const [surveyResults, setSurveyResults] = useState(null);
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    options: ['', ''],
    is_anonymous: true,
    allows_multiple_votes: false,
    end_date: ''
  });
  
  // Stări pentru notificări avansate
  const [showAdvancedNotificationForm, setShowAdvancedNotificationForm] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [newAdvancedNotification, setNewAdvancedNotification] = useState({
    title: '',
    content: '',
    target_grades: [],
    target_names: [],
    notification_type: 'general',
    send_to_all_grades: false,
    send_by_names: false
  });

  useEffect(() => {
    fetchApplications();
    fetchMembers();
    fetchStats();
    fetchTodayBirthdays();
    fetchGradeHierarchy();
    fetchGradeInstructions();
    fetchPlatformStats();
    fetchMembersByCategories();
    fetchSurveys();
    fetchAllNotifications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTodayBirthdays = async () => {
    try {
      const response = await axios.get(`${API}/birthdays/today`);
      setBirthdayMembers(response.data);
    } catch (error) {
      console.error('Error fetching birthday members:', error);
    }
  };

  const fetchGradeHierarchy = async () => {
    try {
      const response = await axios.get(`${API}/admin/grades/hierarchy`);
      setGradeHierarchy(response.data);
    } catch (error) {
      console.error('Error fetching grade hierarchy:', error);
    }
  };

  const fetchAllMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-messages`);
      setAllMessages(response.data);
    } catch (error) {
      console.error('Error fetching all messages:', error);
    }
  };

  const fetchPlatformStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/platform-statistics`);
      setPlatformStats(response.data);
    } catch (error) {
      console.error('Error fetching platform statistics:', error);
    }
  };

  const fetchGradeInstructions = async () => {
    try {
      const response = await axios.get(`${API}/grade-instructions`);
      setGradeInstructions(response.data);
    } catch (error) {
      console.error('Error fetching grade instructions:', error);
    }
  };

  const fetchMembersByCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/members/by-categories`);
      setMembersByCategories(response.data);
    } catch (error) {
      console.error('Error fetching members by categories:', error);
    }
  };

  const fetchMemberDetailedProfile = async (memberId) => {
    try {
      const response = await axios.get(`${API}/admin/members/${memberId}/profile`);
      setSelectedMemberProfile(response.data);
      setShowMemberProfileModal(true);
    } catch (error) {
      alert('Eroare la încărcarea profilului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleCVUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Doar fișiere PDF sunt permise pentru CV-uri');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Fișierul este prea mare. Dimensiunea maximă este 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setCvFile(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadMemberCV = async () => {
    if (!cvFile || !selectedMemberProfile) {
      alert('Selectați un fișier CV');
      return;
    }

    try {
      await axios.put(`${API}/admin/members/${selectedMemberProfile.id}/cv`, {
        cv_document: cvFile
      });
      alert('CV-ul a fost încărcat cu succes!');
      setShowCVUploadModal(false);
      setCvFile(null);
      // Refresh profile to show updated CV status
      fetchMemberDetailedProfile(selectedMemberProfile.id);
    } catch (error) {
      alert('Eroare la încărcarea CV-ului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const downloadMemberCV = async (memberId) => {
    try {
      const response = await axios.get(`${API}/admin/members/${memberId}/cv`);
      const link = document.createElement('a');
      link.href = response.data.cv_document;
      link.download = `CV_${response.data.member_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert('Eroare la descărcarea CV-ului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const deleteMemberCV = async (memberId) => {
    if (window.confirm('Sigur doriți să ștergeți CV-ul acestui membru?')) {
      try {
        await axios.delete(`${API}/admin/members/${memberId}/cv`);
        alert('CV-ul a fost șters cu succes!');
        // Refresh profile to show updated CV status
        fetchMemberDetailedProfile(memberId);
      } catch (error) {
        alert('Eroare la ștergerea CV-ului: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  // Funcții pentru sondaje
  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API}/surveys`);
      setSurveys(response.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const createSurvey = async () => {
    if (!newSurvey.title || !newSurvey.description || newSurvey.options.some(opt => !opt.trim())) {
      alert('Vă rugăm să completați toate câmpurile obligatorii');
      return;
    }

    try {
      const surveyData = {
        ...newSurvey,
        options: newSurvey.options.filter(opt => opt.trim())
      };
      
      await axios.post(`${API}/admin/surveys`, surveyData);
      alert('Sondajul a fost creat cu succes!');
      setNewSurvey({
        title: '',
        description: '',
        options: ['', ''],
        is_anonymous: true,
        allows_multiple_votes: false,
        end_date: ''
      });
      setShowSurveyForm(false);
      fetchSurveys();
    } catch (error) {
      alert('Eroare la crearea sondajului: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  const fetchSurveyResults = async (surveyId) => {
    try {
      const response = await axios.get(`${API}/admin/surveys/${surveyId}/results`);
      setSurveyResults(response.data);
      setShowSurveyResults(surveyId);
    } catch (error) {
      alert('Eroare la obținerea rezultatelor: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  const deleteSurvey = async (surveyId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest sondaj? Această acțiune va șterge și toate voturile.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/surveys/${surveyId}`);
      alert('Sondajul a fost șters cu succes!');
      fetchSurveys();
    } catch (error) {
      alert('Eroare la ștergerea sondajului: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  // Funcții pentru notificări avansate
  const fetchAllNotifications = async () => {
    try {
      // Pentru admin, obținem toate notificările pe care le-a trimis
      // Aici vom simula o listă de notificări trimise
      setAllNotifications([]);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const sendAdvancedNotification = async () => {
    if (!newAdvancedNotification.title || !newAdvancedNotification.content) {
      alert('Vă rugăm să completați titlul și conținutul notificării');
      return;
    }

    if (!newAdvancedNotification.send_to_all_grades && 
        !newAdvancedNotification.send_by_names && 
        newAdvancedNotification.target_grades.length === 0) {
      alert('Vă rugăm să selectați destinatarii notificării');
      return;
    }

    if (newAdvancedNotification.send_by_names && newAdvancedNotification.target_names.length === 0) {
      alert('Vă rugăm să introduceți cel puțin un nume');
      return;
    }

    try {
      await axios.post(`${API}/admin/internal-notifications`, newAdvancedNotification);
      alert('Notificarea a fost trimisă cu succes!');
      setNewAdvancedNotification({
        title: '',
        content: '',
        target_grades: [],
        target_names: [],
        notification_type: 'general',
        send_to_all_grades: false,
        send_by_names: false
      });
      setShowAdvancedNotificationForm(false);
    } catch (error) {
      alert('Eroare la trimiterea notificării: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Sigur doriți să ștergeți această notificare pentru toți membrii?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/notifications/internal/${notificationId}`);
      alert('Notificarea a fost ștearsă cu succes!');
      fetchAllNotifications();
    } catch (error) {
      alert('Eroare la ștergerea notificării: ' + (error.response?.data?.detail || 'Eroare necunoscută'));
    }
  };

  const updateAppSettings = async (settingsData) => {
    try {
      await axios.put(`${API}/admin/app-settings`, settingsData);
      alert('Setările aplicației au fost actualizate cu succes!');
      refreshSettings(); // Actualizează setările globale
      setShowAppSettingsForm(false);
    } catch (error) {
      alert('Eroare la actualizarea setărilor: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const removeLogo = async () => {
    if (window.confirm('Sigur doriți să eliminați logo-ul curent?')) {
      try {
        await axios.delete(`${API}/admin/app-settings/logo`);
        alert('Logo-ul a fost eliminat cu succes!');
        refreshSettings(); // Actualizează setările globale
      } catch (error) {
        alert('Eroare la eliminarea logo-ului: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  const removeBackgroundImage = async () => {
    if (window.confirm('Sigur doriți să eliminați imaginea de fundal curentă? Se va reveni la fundalul default.')) {
      try {
        await axios.delete(`${API}/admin/app-settings/background`);
        alert('Imaginea de fundal a fost eliminată cu succes!');
        refreshSettings(); // Actualizează setările globale
      } catch (error) {
        alert('Eroare la eliminarea imaginii de fundal: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  const resetTheme = async () => {
    if (window.confirm('Sigur doriți să resetați tema la valorile default MLNR 1880?')) {
      try {
        await axios.post(`${API}/admin/app-settings/reset-theme`);
        alert('Tema a fost resetată la valorile default!');
        refreshSettings(); // Actualizează setările globale
      } catch (error) {
        alert('Eroare la resetarea temei: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Noua parolă și confirmarea nu se potrivesc');
      return;
    }

    if (passwordData.new_password.length < 12) {
      alert('Parola nouă trebuie să aibă cel puțin 12 caractere');
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/change-password`, passwordData);
      alert(response.data.message);
      setShowPasswordChange(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      alert('Eroare la schimbarea parolei: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const changeEmail = async () => {
    if (!emailData.current_email || !emailData.new_email || !emailData.password) {
      alert('Toate câmpurile sunt obligatorii pentru schimbarea email-ului');
      return;
    }

    if (emailData.current_email === emailData.new_email) {
      alert('Email-ul nou trebuie să fie diferit de cel curent');
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/change-email`, emailData);
      alert(response.data.message + '\n\nVă rugăm să vă reconectați cu noul email.');
      setShowEmailChange(false);
      setEmailData({
        current_email: '',
        new_email: '',
        password: ''
      });
    } catch (error) {
      alert('Eroare la schimbarea email-ului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const createBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup`);
      setBackupData(response.data);
      
      // Descarcă automat backup-ul ca fișier JSON
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], {type: 'application/json'});
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mlnr1880_backup_${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Backup creat cu succes!\n\nStatistici backup:\n- ${response.data.data.statistics.total_users} utilizatori\n- ${response.data.data.statistics.total_messages} mesaje\n- ${response.data.data.statistics.total_surveys} sondaje\n- ${response.data.data.statistics.total_notifications} notificări\n\nFișierul a fost descărcat automat.`);
    } catch (error) {
      alert('Eroare la crearea backup-ului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    setIsCreatingBackup(false);
  };

  const handleBackupFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          setBackupData(jsonData);
          alert('Fișierul backup a fost încărcat cu succes! Puteți acum să restaurați datele.');
        } catch (error) {
          alert('Fișierul selectat nu este un backup valid. Vă rugăm să selectați un fișier JSON valid.');
        }
      };
      reader.readAsText(file);
    }
  };

  const restoreBackup = async () => {
    if (!backupData) {
      alert('Vă rugăm să încărcați mai întâi un fișier backup');
      return;
    }

    if (!window.confirm(
      '⚠️ ATENȚIE! Restaurarea va ȘTERGE TOATE datele existente și le va înlocui cu cele din backup.\n\n' +
      'Această operație NU POATE FI ANULATĂ!\n\n' +
      'Sunteți absolut sigur că doriți să continuați?'
    )) {
      return;
    }

    if (!window.confirm(
      '🔴 ULTIMA CONFIRMARE!\n\n' +
      'Veți pierde TOATE datele curente din aplicație:\n' +
      '- Toți utilizatorii existenți\n' +
      '- Toate mesajele\n' +
      '- Toate sondajele și voturile\n' +
      '- Toate notificările\n' +
      '- Toate setările\n\n' +
      'Confirmați restaurarea?'
    )) {
      return;
    }

    setIsRestoringBackup(true);
    try {
      const response = await axios.post(`${API}/admin/restore`, backupData);
      alert(
        `✅ Restaurarea a fost finalizată cu succes!\n\n` +
        `Restaurat din backup creat la: ${response.data.backup_timestamp}\n\n` +
        `Rezultate restaurare:\n` +
        `- ${response.data.restore_results.users || 0} utilizatori\n` +
        `- ${response.data.restore_results.messages || 0} mesaje\n` +
        `- ${response.data.restore_results.surveys || 0} sondaje\n` +
        `- ${response.data.restore_results.internal_notifications || 0} notificări\n\n` +
        `Vă rugăm să reîmprospătați pagina pentru a vedea datele restaurate.`
      );
      
      setBackupData(null);
      setShowBackupOptions(false);
      
      // Reîmprospătează datele după restaurare
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      alert('Eroare la restaurarea backup-ului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
    setIsRestoringBackup(false);
  };

  const openMemberPasswordReset = (member) => {
    setSelectedMemberForPasswordReset(member);
    setShowMemberPasswordReset(true);
    setMemberPasswordResetData({
      new_password: '',
      confirm_password: ''
    });
  };

  const resetMemberPassword = async () => {
    if (!memberPasswordResetData.new_password || !memberPasswordResetData.confirm_password) {
      alert('Toate câmpurile sunt obligatorii');
      return;
    }

    if (memberPasswordResetData.new_password !== memberPasswordResetData.confirm_password) {
      alert('Parola și confirmarea nu se potrivesc');
      return;
    }

    if (memberPasswordResetData.new_password.length < 8) {
      alert('Parola trebuie să aibă cel puțin 8 caractere');
      return;
    }

    if (!window.confirm(
      `Sunteți sigur că doriți să resetați parola pentru ${selectedMemberForPasswordReset.first_name} ${selectedMemberForPasswordReset.last_name}?\n\n` +
      `Membrul va trebui să se reconecteze cu noua parolă.`
    )) {
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/members/${selectedMemberForPasswordReset.id}/reset-password`, 
        {
          member_id: selectedMemberForPasswordReset.id,
          new_password: memberPasswordResetData.new_password
        }
      );
      
      alert(
        `✅ ${response.data.message}\n\n` +
        `📧 Email membru: ${response.data.member_email}\n` +
        `🔑 Noua parolă: ${memberPasswordResetData.new_password}\n\n` +
        `⚠️ Informați membrul despre noua parolă și cerețu-i să se reconecteze.`
      );
      
      setShowMemberPasswordReset(false);
      setSelectedMemberForPasswordReset(null);
      setMemberPasswordResetData({
        new_password: '',
        confirm_password: ''
      });
      
    } catch (error) {
      alert('Eroare la resetarea parolei: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Funcție pentru editarea instrucțiunilor de grad
  const editInstruction = (instruction) => {
    setEditingInstruction(instruction);
    setNewInstruction({
      grade: instruction.grade,
      title: instruction.title,
      description: instruction.description,
      pdf_content: '' // Se va încărca doar dacă se schimbă PDF-ul
    });
    setShowEditInstruction(true);
  };

  const updateInstruction = async () => {
    if (!newInstruction.title || !newInstruction.description) {
      alert('Vă rugăm să completați toate câmpurile obligatorii');
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/grade-instructions/${editingInstruction.id}`, newInstruction);
      alert(`✅ ${response.data.message}\n\nActualizat de: ${response.data.updated_by}\nLa: ${new Date(response.data.updated_at).toLocaleString('ro-RO')}`);
      
      setShowEditInstruction(false);
      setEditingInstruction(null);
      setNewInstruction({grade: '', title: '', description: '', pdf_content: ''});
      fetchGradeInstructions(); // Refresh lista
    } catch (error) {
      alert('Eroare la actualizarea instrucțiunii: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Funcție pentru ștergerea instrucțiunilor de grad
  const deleteInstruction = async (instruction) => {
    if (!window.confirm(
      `Sunteți sigur că doriți să ștergeți instrucțiunea?\n\n` +
      `Titlu: ${instruction.title}\n` +
      `Grad: ${instruction.grade}\n\n` +
      `Această acțiune nu poate fi anulată!`
    )) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/grade-instructions/${instruction.id}`);
      alert(`✅ ${response.data.message}\n\nȘters de: ${response.data.deleted_by}\nLa: ${new Date(response.data.deleted_at).toLocaleString('ro-RO')}`);
      fetchGradeInstructions(); // Refresh lista
    } catch (error) {
      alert('Eroare la ștergerea instrucțiunii: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Funcție pentru descărcarea documentelor aplicațiilor
  const downloadApplicationDocument = async (applicationId, documentType, applicantName) => {
    try {
      const response = await axios.get(`${API}/admin/applications/${applicationId}/download/${documentType}`);
      const documentData = response.data;
      
      // Convertește base64 în blob pentru descărcare
      const binaryString = atob(documentData.document_content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Creează link pentru descărcare
      const link = document.createElement('a');
      link.href = url;
      
      const documentName = documentType === 'intention_letter' ? 'Scrisoare_Intentie' : 'Cazier_Judiciar';
      const fileName = `${applicantName.replace(/\s+/g, '_')}_${documentName}_MLNR1880.pdf`;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`✅ Documentul a fost descărcat cu succes!\n\nDescarcat de: ${documentData.download_info.downloaded_by}\nLa: ${new Date(documentData.download_info.downloaded_at).toLocaleString('ro-RO')}`);
      
    } catch (error) {
      alert('Eroare la descărcarea documentului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const updateApplication = async (applicationId, status, notes = '') => {
    try {
      await axios.put(`${API}/applications/${applicationId}`, {
        status,
        admin_notes: notes
      });
      alert('Aplicația a fost actualizată cu succes!');
      fetchApplications();
      fetchStats();
    } catch (error) {
      alert('Eroare la actualizarea aplicației: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const sendBirthdayNotification = async (userId) => {
    const defaultMessage = "🎂 La mulți ani! Vă urăm sănătate, fericire și realizări în noul an de viață! Frații din MLNR 1880 vă transmit cele mai calde urări! 🎉";

    try {
      const formData = new FormData();
      formData.append('message', defaultMessage);
      
      await axios.post(`${API}/birthdays/send/${userId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return true; // Succès
    } catch (error) {
      console.error('Eroare trimitere notificare birthday:', error);
      throw error; // Re-throw pentru gestionarea în funcția apelantă
    }
  };

  const addMember = async () => {
    if (!newMemberData.email || !newMemberData.password || !newMemberData.first_name || !newMemberData.last_name) {
      alert('Vă rugăm să completați toate câmpurile obligatorii');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/members?role=${newMemberData.role}`, newMemberData);
      alert(response.data.message);
      setShowAddMemberForm(false);
      setNewMemberData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        birth_date: '',
        role: 'member'
      });
      fetchMembers();
      fetchStats();
    } catch (error) {
      alert('Eroare la adăugarea membrului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const updateMember = async () => {
    if (!editingMember) return;

    try {
      await axios.put(`${API}/admin/members/${editingMember.id}`, newMemberData);
      alert('Profilul a fost actualizat cu succes!');
      setEditingMember(null);
      setShowAddMemberForm(false);
      setNewMemberData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        birth_date: '',
        role: 'member'
      });
      fetchMembers();
    } catch (error) {
      alert('Eroare la actualizarea profilului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const deleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Sigur doriți să eliminați din MLNR 1880 pe ${memberName}? Această acțiune nu poate fi anulată.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/members/${memberId}`);
      alert(response.data.message);
      fetchMembers();
      fetchStats();
    } catch (error) {
      alert('Eroare la ștergerea membrului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const changeRole = async (memberId, memberName, newRole) => {
    if (!window.confirm(`Sigur doriți să schimbați rolul pentru ${memberName}?`)) {
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/members/${memberId}/role?new_role=${newRole}`);
      alert(response.data.message);
      fetchMembers();
      fetchStats();
    } catch (error) {
      alert('Eroare la schimbarea rolului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const startEditMember = (member) => {
    setEditingMember(member);
    setNewMemberData({
      email: member.email,
      password: '',
      first_name: member.first_name,
      last_name: member.last_name,
      birth_date: member.birth_date ? member.birth_date.split('T')[0] : '',
      role: member.role
    });
    setShowAddMemberForm(true);
  };

  const viewDocument = async (applicationId, documentType) => {
    try {
      const response = await axios.get(`${API}/admin/applications/${applicationId}/document/${documentType}`);
      const document = response.data;
      
      // Convertește base64 în blob pentru vizualizare
      const binaryString = atob(document.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Deschide documentul într-o fereastră nouă
      const newWindow = window.open();
      newWindow.document.write(`
        <html>
          <head>
            <title>Document ${documentType} - MLNR 1880</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .header { background: #1e40af; color: white; padding: 15px; margin-bottom: 20px; border-radius: 8px; }
              .content { border: 1px solid #ccc; border-radius: 8px; overflow: hidden; }
              iframe { width: 100%; height: 80vh; border: none; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>📄 Document ${documentType} - MLNR 1880</h2>
              <p>ID Aplicație: ${applicationId}</p>
              <p>Tip Document: ${document.document_type}</p>
            </div>
            <div class="content">
              <iframe src="${url}" title="Document PDF"></iframe>
            </div>
            <script>
              window.addEventListener('beforeunload', function() {
                URL.revokeObjectURL('${url}');
              });
            </script>
          </body>
        </html>
      `);
      newWindow.document.close();
    } catch (error) {
      alert('Eroare la încărcarea documentului: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const assignGrade = async (memberId, grade) => {
    try {
      console.log('Trimit request pentru atribuirea gradului:', { memberId, grade });
      console.log('URL:', `${API}/admin/members/${memberId}/grade`);
      console.log('Payload:', { masonic_grade: grade });
      
      const response = await axios.put(`${API}/admin/members/${memberId}/grade`, { masonic_grade: grade });
      
      console.log('Răspuns primit:', response.data);
      alert(response.data.message || 'Gradul masonic a fost atribuit cu succes!');
      setShowGradeSelector({show: false, member: null});
      setSelectedGrade('');
      fetchMembers();
    } catch (error) {
      console.error('Eroare completă:', error);
      console.error('Răspuns eroare:', error.response);
      
      let errorMessage = 'Eroare necunoscută';
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        errorMessage = error.response.data?.detail || error.response.data?.message || `Eroare HTTP ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Nu s-a putut conecta la server';
      } else {
        errorMessage = error.message;
      }
      
      alert('Eroare la atribuirea gradului: ' + errorMessage);
    }
  };

  const toggleMemberStatus = async (memberId, currentStatus) => {
    try {
      const response = await axios.put(`${API}/admin/members/${memberId}/status`, { is_active: !currentStatus });
      alert(response.data.message || `Membrul a fost ${!currentStatus ? 'activat' : 'dezactivat'} cu succes!`);
      fetchMembers();
    } catch (error) {
      console.error('Eroare la schimbarea statusului:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Eroare necunoscută';
      alert('Eroare la schimbarea statusului: ' + errorMessage);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Prezentare generală
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Aplicații
              {stats.pending_applications > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                  {stats.pending_applications}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'members'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Membri
            </button>
            <button
              onClick={() => setActiveTab('birthdays')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'birthdays'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Zile de naștere
              {birthdayMembers.length > 0 && (
                <span className="ml-2 bg-green-500 text-white text-xs rounded-full px-2 py-1">
                  {birthdayMembers.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('grades')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'grades'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Grade Masonice
            </button>
            <button
              onClick={() => {
                setActiveTab('categories');
                fetchMembersByCategories();
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🏛️ Membri pe Categorii
            </button>
            <button
              onClick={() => setActiveTab('instructions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'instructions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Instrucțiuni Grade
            </button>
            <button
              onClick={() => {
                setActiveTab('monitoring');
                fetchAllMessages();
                fetchPlatformStats();
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monitoring'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔍 Monitorizare Completă
            </button>
            <button
              onClick={() => {
                setActiveTab('surveys');
                fetchSurveys();
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'surveys'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Sondaje
            </button>
            <button
              onClick={() => setActiveTab('advanced-notifications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'advanced-notifications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📢 Notificări Avansate
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚙️ Setări Admin
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Panoul Maestrului Venerat - MLNR 1880</h2>
              
              <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4">
                <p className="text-purple-800">
                  <strong>Maestru Venerat</strong> - Aveți control complet asupra Marii Loji Naționale Române MLNR 1880. 
                  Gestionați candidații, frații și activitățile Lojei.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Frați Activi MLNR 1880</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.total_members || 0}</p>
                  <p className="text-blue-600 text-sm">membri confirmați</p>
                </div>
                
                <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-600">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">Candidați în Evaluare</h3>
                  <p className="text-3xl font-bold text-yellow-600">{stats.total_candidates || 0}</p>
                  <p className="text-yellow-600 text-sm">cereri în studiu</p>
                </div>
                
                <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Dosare Pendinte</h3>
                  <p className="text-3xl font-bold text-red-600">{stats.pending_applications || 0}</p>
                  <p className="text-red-600 text-sm">necesită decizia Maestrului Venerat</p>
                </div>
              </div>

              {birthdayMembers.length > 0 && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    🎉 Zile de naștere astăzi ({birthdayMembers.length})
                  </h3>
                  <div className="space-y-1">
                    {birthdayMembers.map((member) => (
                      <p key={member.id} className="text-green-700">
                        {member.first_name} {member.last_name}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'applications' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Gestionare aplicații</h2>
              
              <div className="space-y-4">
                {applications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nu există aplicații de procesat.</p>
                ) : (
                  applications.map((application) => (
                    <div key={application.id} className="border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">
                            {application.user.first_name} {application.user.last_name}
                          </h3>
                          <p className="text-gray-600">{application.user.email}</p>
                          <p className="text-sm text-gray-500">
                            Trimisă la: {new Date(application.created_at).toLocaleString('ro-RO')}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          application.status === 'approved' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {application.status === 'pending' ? 'În așteptare' :
                           application.status === 'approved' ? 'Aprobată' : 'Respinsă'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">📄 Scrisoare de Intenție</h4>
                          <div className="space-y-2">
                            <p className="text-xs text-gray-600">
                              Status: {application.intention_letter ? '✅ Încărcat' : '❌ Nu este încărcat'}
                            </p>
                            {application.intention_letter && (
                              <>
                                <p className="text-xs text-gray-500">
                                  Fișier: {application.intention_letter.filename || 'Document.pdf'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Încărcat: {application.intention_letter.uploaded_at ? 
                                    new Date(application.intention_letter.uploaded_at).toLocaleDateString('ro-RO') : 
                                    'Data necunoscută'}
                                </p>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => viewDocument(application.id, 'intention_letter')}
                                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
                                    title="Vezi documentul în browser"
                                  >
                                    👁️ Vezi
                                  </button>
                                  <button
                                    onClick={() => downloadApplicationDocument(
                                      application.id, 
                                      'intention_letter', 
                                      `${application.user.first_name}_${application.user.last_name}`
                                    )}
                                    className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded transition-colors"
                                    title="Descarcă documentul"
                                  >
                                    💾 Descarcă
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h4 className="text-sm font-medium text-red-800 mb-2">📋 Cazier Judiciar</h4>
                          <div className="space-y-2">
                            <p className="text-xs text-gray-600">
                              Status: {application.criminal_record ? '✅ Încărcat' : '❌ Nu este încărcat'}
                            </p>
                            {application.criminal_record && (
                              <>
                                <p className="text-xs text-gray-500">
                                  Fișier: {application.criminal_record.filename || 'Document.pdf'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Încărcat: {application.criminal_record.uploaded_at ? 
                                    new Date(application.criminal_record.uploaded_at).toLocaleDateString('ro-RO') : 
                                    'Data necunoscută'}
                                </p>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => viewDocument(application.id, 'criminal_record')}
                                    className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors"
                                    title="Vezi documentul în browser"
                                  >
                                    👁️ Vezi
                                  </button>
                                  <button
                                    onClick={() => downloadApplicationDocument(
                                      application.id, 
                                      'criminal_record', 
                                      `${application.user.first_name}_${application.user.last_name}`
                                    )}
                                    className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded transition-colors"
                                    title="Descarcă documentul"
                                  >
                                    💾 Descarcă
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {(application.intention_letter || application.criminal_record) && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                          <h5 className="text-sm font-medium text-green-800 mb-2">📂 Sumar Documente</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                              <span>Scrisoare de Intenție:</span>
                              <span className={application.intention_letter ? 'text-green-600' : 'text-red-600'}>
                                {application.intention_letter ? '✅ Disponibil' : '❌ Lipsește'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Cazier Judiciar:</span>
                              <span className={application.criminal_record ? 'text-green-600' : 'text-red-600'}>
                                {application.criminal_record ? '✅ Disponibil' : '❌ Lipsește'}
                              </span>
                            </div>
                          </div>
                          
                          {(application.intention_letter && application.criminal_record) && (
                            <div className="mt-2 p-2 bg-green-100 rounded text-center">
                              <p className="text-xs text-green-800 font-medium">
                                🎯 Toate documentele sunt complete și pot fi procesate
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {application.admin_notes && (
                        <div className="bg-gray-50 p-3 rounded mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-1">Note administrator:</p>
                          <p className="text-sm text-gray-600">{application.admin_notes}</p>
                        </div>
                      )}

                      {application.status === 'pending' && (
                        <div className="flex space-x-3">
                          <button
                            onClick={() => {
                              const notes = prompt('Note opționale pentru aprobare:');
                              updateApplication(application.id, 'approved', notes || '');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                          >
                            Aprobă
                          </button>
                          <button
                            onClick={() => {
                              const notes = prompt('Motivul respingerii (opțional):');
                              updateApplication(application.id, 'rejected', notes || '');
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                          >
                            Respinge
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Gestionare Frați MLNR 1880</h2>
                <button
                  onClick={() => setShowAddMemberForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  ➕ Adaugă Frate
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member) => (
                  <div key={member.id} className="bg-white border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300">
                    <div className="flex justify-between items-start mb-4">
                      <div onClick={() => fetchMemberDetailedProfile(member.id)} className="flex-1">
                        <h3 className="text-lg font-semibold">
                          {member.role === 'admin' ? 'Maestru Venerat' : 'F∴'} {member.first_name} {member.last_name}
                        </h3>
                        <p className="text-gray-600">{member.email}</p>
                        <div className="text-sm text-gray-500 mt-1">
                          <p className="capitalize">
                            {member.role === 'admin' ? 'Maestru Venerat' : 
                             member.role === 'member' ? 'Frate' : 'Candidat'}
                          </p>
                          {member.masonic_grade && (
                            <p className="text-purple-600 font-medium">
                              {getGradeDisplayName(member.masonic_grade)}
                            </p>
                          )}
                          {!member.masonic_grade && member.role === 'member' && (
                            <p className="text-red-500 text-xs">Fără grad masonic</p>
                          )}
                        </div>
                        <div className="mt-2 text-xs text-blue-600">
                          👁️ Click pentru profil complet
                        </div>
                      </div>
                      <div className="flex flex-col space-y-1">
                        <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => startEditMember(member)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            title="Editează"
                          >
                            ✏️
                          </button>
                          {member.role !== 'admin' && (
                            <button
                              onClick={() => setShowGradeSelector({show: true, member})}
                              className="text-purple-600 hover:text-purple-800 text-sm"
                              title="Atribuie Grad Masonic"
                            >
                              🏛️
                            </button>
                          )}
                          {member.role !== 'admin' && (
                            <button
                              onClick={() => openMemberPasswordReset(member)}
                              className="text-yellow-600 hover:text-yellow-800 text-sm"
                              title="Resetează Parola"
                            >
                              🔑
                            </button>
                          )}
                          {member.role !== 'admin' && (
                            <button
                              onClick={() => deleteMember(member.id, `${member.first_name} ${member.last_name}`)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Șterge"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-400 space-y-1">
                      <p>Membru din: {new Date(member.created_at).toLocaleDateString('ro-RO')}</p>
                      {member.birth_date && (
                        <p>Născut: {new Date(member.birth_date).toLocaleDateString('ro-RO')}</p>
                      )}
                      <p>Status: {member.is_active ? '🟢 Activ' : '🔴 Inactiv'}</p>
                    </div>
                    
                    {member.role !== 'admin' && (
                      <div className="mt-4 space-y-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex space-x-2">
                          {member.role === 'candidate' && (
                            <button
                              onClick={() => changeRole(member.id, `${member.first_name} ${member.last_name}`, 'member')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
                            >
                              Promovează la Frate
                            </button>
                          )}
                          {member.role === 'member' && (
                            <button
                              onClick={() => changeRole(member.id, `${member.first_name} ${member.last_name}`, 'candidate')}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-2 py-1 rounded"
                            >
                              Retrogradează la Candidat
                            </button>
                          )}
                          <button
                            onClick={() => toggleMemberStatus(member.id, member.is_active)}
                            className={`text-white text-xs px-2 py-1 rounded ${
                              member.is_active ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                            }`}
                          >
                            {member.is_active ? 'Dezactivează' : 'Activează'}
                          </button>
                        </div>
                        {member.role === 'member' && (
                          <button
                            onClick={() => setShowGradeSelector({show: true, member})}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 py-1 rounded"
                          >
                            🏛️ Atribuie/Modifică Grad Masonic
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'birthdays' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">🎂 Gestionare Zile de Naștere MLNR 1880</h2>
              
              {/* Butoane de acțiuni */}
              <div className="mb-6 flex flex-wrap gap-4">
                <button
                  onClick={() => fetchTodayBirthdays()}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  🔄 Actualizează Lista
                </button>
                <button
                  onClick={async () => {
                    if (birthdayMembers.length === 0) {
                      alert('Nu sunt zile de naștere astăzi');
                      return;
                    }
                    try {
                      const promises = birthdayMembers.map(member => sendBirthdayNotification(member.id));
                      await Promise.all(promises);
                      alert(`✅ Felicitări trimise pentru ${birthdayMembers.length} frați!`);
                    } catch (error) {
                      alert('❌ Eroare la trimiterea felicitărilor: ' + error.message);
                    }
                  }}
                  disabled={birthdayMembers.length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🎉 Trimite Felicitări la TOȚI
                </button>
                <button
                  onClick={() => {
                    setActiveTab('advanced-notifications');
                    setNewAdvancedNotification({
                      ...newAdvancedNotification,
                      title: '🎂 Felicitări de Ziua de Naștere',
                      content: 'La mulți ani și sănătate! Să aveți parte de multe bucurii și realizări în noul an de viață!',
                      notification_type: 'birthday'
                    });
                    setShowAdvancedNotificationForm(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  📢 Felicitare Personalizată
                </button>
              </div>

              {/* Mesaj personalizat */}
              <div className="mb-6 bg-white border rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Mesaj personalizat pentru ziua de naștere:
                </label>
                <textarea
                  value={birthdayMessage}
                  onChange={(e) => setBirthdayMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Introduceți mesajul pentru ziua de naștere..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Acest mesaj va fi trimis automat fraților în ziua lor de naștere
                </p>
              </div>

              {/* Statistici generale */}
              <div className="mb-6 bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">📊 Statistici Generale</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{birthdayMembers.length}</p>
                    <p className="text-green-800 text-sm">Zile de naștere astăzi</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {(() => {
                        const thisMonth = new Date().getMonth() + 1;
                        return members.filter(m => {
                          if (!m.birth_date) return false;
                          const memberMonth = new Date(m.birth_date).getMonth() + 1;
                          return memberMonth === thisMonth;
                        }).length;
                      })()}
                    </p>
                    <p className="text-blue-800 text-sm">Zile de naștere luna aceasta</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {members.filter(m => m.birth_date).length}
                    </p>
                    <p className="text-purple-800 text-sm">Membri cu date de naștere</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {members.filter(m => !m.birth_date).length}
                    </p>
                    <p className="text-orange-800 text-sm">Membri fără date de naștere</p>
                  </div>
                </div>
              </div>

              {/* Zile de naștere astăzi */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">🎉 Zile de Naștere Astăzi</h3>
                {birthdayMembers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-gray-400 text-6xl mb-4">🎂</div>
                    <p className="text-gray-500 text-lg">Nu sunt zile de naștere astăzi</p>
                    <p className="text-gray-400 text-sm mt-2">Frații vor fi notificați automat în ziua lor specială</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {birthdayMembers.map((member) => (
                      <div key={member.id} className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
                        <div className="text-center">
                          <div className="text-5xl mb-3">🎉</div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            F∴ {member.first_name} {member.last_name}
                          </h3>
                          <p className="text-green-600 font-medium text-sm">
                            {member.masonic_grade ? getGradeDisplayName(member.masonic_grade) : 'Fără grad masonic'}
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            📅 {new Date(member.birth_date).toLocaleDateString('ro-RO', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-blue-600 text-xs mt-1">
                            ✨ {new Date().getFullYear() - new Date(member.birth_date).getFullYear()} de ani
                          </p>
                          <div className="mt-4 space-y-2">
                            <button
                              onClick={() => sendBirthdayNotification(member.id)}
                              className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
                            >
                              🎂 Trimite Felicitare
                            </button>
                            <button
                              onClick={() => fetchMemberDetailedProfile(member.id)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
                            >
                              👤 Vezi Profil Complet
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Zile de naștere următoare */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">📅 Următoarele Zile de Naștere (Această Lună)</h3>
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-2">
                    {(() => {
                      const today = new Date();
                      const thisMonth = today.getMonth();
                      const thisYear = today.getFullYear();
                      
                      const upcomingBirthdays = members
                        .filter(m => {
                          if (!m.birth_date) return false;
                          const birthDate = new Date(m.birth_date);
                          const thisYearBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
                          return birthDate.getMonth() === thisMonth && thisYearBirthday > today;
                        })
                        .sort((a, b) => {
                          const dateA = new Date(a.birth_date);
                          const dateB = new Date(b.birth_date);
                          return dateA.getDate() - dateB.getDate();
                        });

                      if (upcomingBirthdays.length === 0) {
                        return (
                          <p className="text-gray-500 text-center py-8">
                            Nu mai sunt zile de naștere în această lună
                          </p>
                        );
                      }

                      return upcomingBirthdays.map((member) => {
                        const birthDate = new Date(member.birth_date);
                        const daysUntil = Math.ceil((new Date(thisYear, birthDate.getMonth(), birthDate.getDate()) - today) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center space-x-3">
                              <div className="text-2xl">🎂</div>
                              <div>
                                <p className="font-medium text-gray-800">
                                  F∴ {member.first_name} {member.last_name}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {member.masonic_grade ? getGradeDisplayName(member.masonic_grade) : 'Fără grad masonic'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-blue-600">
                                {birthDate.getDate()} {birthDate.toLocaleDateString('ro-RO', { month: 'long' })}
                              </p>
                              <p className="text-xs text-gray-500">
                                peste {daysUntil} {daysUntil === 1 ? 'zi' : 'zile'}
                              </p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'grades' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">🏛️ Grade Masonice MLNR 1880</h2>
              
              <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4">
                <p className="text-purple-800">
                  <strong>Sistemul celor 33 de Grade Masonice</strong> - Ierarhia completă implementată în MLNR 1880.
                </p>
              </div>

              {/* Butoane de acțiuni pentru grade */}
              <div className="mb-6 flex flex-wrap gap-4">
                <button
                  onClick={() => setShowInstructionUpload(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  📚 Adaugă Instrucțiune Grad
                </button>
                <button
                  onClick={() => setShowGradeNotificationForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  📢 Notificare Grade Specifice
                </button>
                <button
                  onClick={() => {
                    setActiveTab('categories');
                    fetchMembersByCategories();
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  👥 Vezi Membri pe Categorii
                </button>
                <button
                  onClick={() => {
                    setActiveTab('instructions');
                    fetchGradeInstructions();
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  📖 Gestionează Instrucțiuni
                </button>
              </div>

              {/* Ierarhia gradelor îmbunătățită */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {Object.entries(gradeHierarchy).map(([category, grades]) => (
                  <div key={category} className="bg-white border rounded-lg p-4 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3 text-blue-800 border-b border-blue-200 pb-2">
                      {category === 'Simbolic' && '🔺'} 
                      {category === 'Capitular' && '🔶'} 
                      {category === 'Filosofic' && '⭐'} 
                      {category === 'Administrativ' && '👑'} 
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {grades.map((grade) => (
                        <div key={grade.grade} className="text-sm border-l-2 border-blue-200 pl-2 hover:bg-blue-50 p-1 rounded transition-colors">
                          <div className="font-medium text-gray-800">{grade.level}° - {grade.display_name}</div>
                          <div className="text-xs text-gray-500">
                            {(() => {
                              const count = members.filter(m => m.masonic_grade === grade.grade).length;
                              return count > 0 ? `${count} membri` : 'Fără membri';
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Statistici generale grade */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">📊 Statistici Grade</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {members.filter(m => m.masonic_grade && parseInt(m.masonic_grade) <= 3).length}
                    </p>
                    <p className="text-blue-800 text-sm">Grade Simbolice (1°-3°)</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {members.filter(m => m.masonic_grade && parseInt(m.masonic_grade) >= 4 && parseInt(m.masonic_grade) <= 18).length}
                    </p>
                    <p className="text-green-800 text-sm">Grade Capitulare (4°-18°)</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {members.filter(m => m.masonic_grade && parseInt(m.masonic_grade) >= 19 && parseInt(m.masonic_grade) <= 30).length}
                    </p>
                    <p className="text-purple-800 text-sm">Grade Filosofice (19°-30°)</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">
                      {members.filter(m => m.masonic_grade && parseInt(m.masonic_grade) >= 31).length}
                    </p>
                    <p className="text-red-800 text-sm">Grade Administrative (31°-33°)</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Membri fără grad:</span>
                    <span className="font-medium text-gray-900">
                      {members.filter(m => !m.masonic_grade && m.role === 'member').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-700">Total membri cu grade:</span>
                    <span className="font-medium text-gray-900">
                      {members.filter(m => m.masonic_grade).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">🏛️ Membri MLNR 1880 pe Categorii Masonice</h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Organizarea Fraților pe Categorii</strong> - Vizualizați membrii grupați pe categoriile de grade masonice.
                </p>
              </div>

              {Object.keys(membersByCategories).length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Se încarcă datele despre membri...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {Object.entries(membersByCategories).map(([categoryName, categoryData]) => (
                    <div key={categoryName} className="bg-white border rounded-lg shadow-sm">
                      <div className="border-b bg-gray-50 px-6 py-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">
                              {categoryName === 'Simbolic' && '🔺'} 
                              {categoryName === 'Capitular' && '🔶'} 
                              {categoryName === 'Filosofic' && '⭐'} 
                              {categoryName === 'Administrativ' && '👑'} 
                              {categoryName === 'Fără Grad' && '🆕'} 
                              {categoryName}
                            </h3>
                            <p className="text-sm text-gray-600">{categoryData.category_description}</p>
                          </div>
                          <div className="text-right">
                            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                              {categoryData.count} {categoryData.count === 1 ? 'membru' : 'membri'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-6">
                        {categoryData.members.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">Nu există membri în această categorie.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categoryData.members.map((member) => (
                              <div 
                                key={member.id} 
                                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                                onClick={() => fetchMemberDetailedProfile(member.id)}
                              >
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    {member.profile_photo ? (
                                      <img 
                                        src={member.profile_photo} 
                                        alt={`${member.first_name} ${member.last_name}`}
                                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                        <span className="text-gray-600 font-medium text-sm">
                                          {member.first_name[0]}{member.last_name[0]}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-gray-800 truncate">
                                      {member.first_name} {member.last_name}
                                    </h4>
                                    <p className="text-xs text-gray-500 truncate">{member.email}</p>
                                    
                                    <div className="mt-2 space-y-1">
                                      <div className="flex items-center space-x-2">
                                        <span className={`inline-block w-2 h-2 rounded-full ${
                                          member.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'
                                        }`}></span>
                                        <span className="text-xs font-medium text-gray-700">
                                          {member.role === 'admin' ? 'Administrator' : 'Membru'}
                                        </span>
                                      </div>
                                      
                                      <div className="text-xs text-purple-600 font-medium">
                                        {member.grade_display_name}
                                        {member.grade_level > 0 && (
                                          <span className="ml-1 text-gray-500">({member.grade_level}°)</span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="mt-2 text-xs text-blue-600">
                                      👁️ Click pentru detalii complete
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'instructions' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📚 Instrucțiuni Grade Masonice</h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Management Instrucțiuni</strong> - Administrați instrucțiunile pentru fiecare grad masonic.
                </p>
              </div>

              <div className="mb-6">
                <button
                  onClick={() => setShowInstructionUpload(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors mr-4"
                >
                  ➕ Adaugă Instrucțiune Nouă
                </button>
                <button
                  onClick={() => setShowGradeNotificationForm(true)}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  📢 Trimite Notificare Grade
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {gradeInstructions.map((instruction) => (
                  <div key={instruction.id} className="bg-white border rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{instruction.title}</h3>
                    <p className="text-sm text-purple-600 mb-2">{instruction.grade_display_name}</p>
                    <p className="text-gray-600 text-sm mb-3">{instruction.description}</p>
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">
                          Creat: {new Date(instruction.created_at).toLocaleDateString('ro-RO')}
                        </span>
                        {instruction.updated_at && (
                          <span className="text-xs text-blue-600">
                            Actualizat: {new Date(instruction.updated_at).toLocaleDateString('ro-RO')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(instruction.pdf_content, '_blank')}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-2 rounded"
                          title="Vezi PDF-ul instrucțiunii"
                        >
                          📄 Vezi PDF
                        </button>
                        <button
                          onClick={() => editInstruction(instruction)}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs py-1 px-2 rounded"
                          title="Editează instrucțiunea"
                        >
                          ✏️ Editează
                        </button>
                        <button
                          onClick={() => deleteInstruction(instruction)}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs py-1 px-2 rounded"
                          title="Șterge instrucțiunea"
                        >
                          🗑️ Șterge
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'monitoring' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">🔍 Monitorizare Completă MLNR 1880</h2>
              
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
                <p className="text-red-800">
                  <strong>Funcție Administrativă Sensibilă</strong> - Monitorizarea completă a activității din MLNR 1880.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Total Utilizatori</h3>
                  <p className="text-2xl font-bold text-blue-600">{platformStats.total_users || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Total Mesaje</h3>
                  <p className="text-2xl font-bold text-green-600">{platformStats.total_messages || 0}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-800 mb-2">Cereri Ajutor</h3>
                  <p className="text-2xl font-bold text-purple-600">{platformStats.help_requests || 0}</p>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">🗂️ Istoric Mesaje Platformă</h3>
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {allMessages.map((message) => (
                    <div key={message.id} className="border-b pb-3">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-sm">
                          {message.sender?.first_name} {message.sender?.last_name}
                          {message.sender?.role === 'admin' && ' (Maestru Venerat)'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(message.created_at).toLocaleString('ro-RO')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{message.content}</p>
                      {message.is_help_request && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded mt-1 inline-block">
                          Cerere Ajutor
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'surveys' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📊 Gestionare Sondaje MLNR 1880</h2>
              
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-800">
                  <strong>Sondaje pentru Membri</strong> - Creați și gestionați sondaje pentru a consulta opiniile fraților.
                </p>
              </div>

              <div className="mb-6">
                <button
                  onClick={() => setShowSurveyForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  ➕ Creează Sondaj Nou
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {surveys.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    Nu există sondaje create încă.
                  </div>
                ) : (
                  surveys.map((survey) => (
                    <div key={survey.id} className="bg-white border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">{survey.title}</h3>
                          <p className="text-gray-600 text-sm mt-1">{survey.description}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => fetchSurveyResults(survey.id)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded transition-colors"
                          >
                            📈 Rezultate
                          </button>
                          <button
                            onClick={() => deleteSurvey(survey.id)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded transition-colors"
                          >
                            🗑️ Șterge
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <p className="text-sm font-medium text-gray-700">Opțiuni:</p>
                        {survey.options.map((option, index) => (
                          <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                            {index + 1}. {option}
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          <span>📊 {survey.total_votes || 0} voturi</span>
                          <span>{survey.is_anonymous ? '🎭 Anonim' : '👤 Cu nume'}</span>
                          <span>{survey.allows_multiple_votes ? '🔄 Voturi multiple' : '1️⃣ Un vot'}</span>
                        </div>
                        <span className="text-xs">
                          {new Date(survey.created_at).toLocaleDateString('ro-RO')}
                        </span>
                      </div>

                      {survey.end_date && (
                        <div className="mt-2 text-xs text-orange-600">
                          ⏰ Expiră: {new Date(survey.end_date).toLocaleDateString('ro-RO')}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'advanced-notifications' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📢 Notificări Avansate MLNR 1880</h2>
              
              <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4">
                <p className="text-purple-800">
                  <strong>Notificări Avansate</strong> - Trimiteți notificări către membri pe baza gradelor sau numelor specifice.
                </p>
              </div>

              <div className="mb-6">
                <button
                  onClick={() => setShowAdvancedNotificationForm(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  📨 Trimite Notificare Avansată
                </button>
              </div>

              <div className="mt-6 bg-gray-50 border rounded-lg p-4">
                <h4 className="font-semibold mb-3">ℹ️ Informații despre Notificările Avansate</h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>📊 Toate gradele:</strong> Trimite către toți membrii activi cu grade de la 1° la 33°</p>
                  <p><strong>🎖️ Grade specifice:</strong> Selectați individual gradele către care doriți să trimiteți</p>
                  <p><strong>👥 Pe nume:</strong> Introduceți numele complete pentru trimitere precisă</p>
                  <p><strong>🔊 Notificări audio:</strong> Membrii vor primi și sunet de notificare dacă au activată funcția</p>
                  <p><strong>🗑️ Ștergere:</strong> Puteți șterge notificările trimise pentru toți membrii din istoricul de notificări</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800">⚙️ Setări Admin MLNR 1880</h2>
              
              <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="text-yellow-800">
                  <strong>Setări Administrative</strong> - Configurări pentru Maestru Venerat și funcțiuni sensibile.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">🔒 Securitate Cont</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowPasswordChange(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Schimbă Parola Admin
                    </button>
                    <button
                      onClick={() => setShowEmailChange(true)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Schimbă Email Admin
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded mt-3">
                    <strong>Securitate:</strong> Folosiți parole complexe și email-uri sigure.
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">📊 Informații Sistem</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total Admini:</span>
                      <span className="font-medium">{platformStats.total_admins || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Membri:</span>
                      <span className="font-medium">{platformStats.total_members || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Candidați:</span>
                      <span className="font-medium">{platformStats.total_candidates || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Aplicații Pending:</span>
                      <span className="font-medium">{platformStats.pending_applications || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">📈 Dashboard Live</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-green-50 p-3 rounded text-center">
                        <div className="text-green-600 font-medium">Total Membri</div>
                        <div className="text-xl font-bold text-green-800">{platformStats.users?.members || stats.total_members || 0}</div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded text-center">
                        <div className="text-blue-600 font-medium">Zile Naștere</div>
                        <div className="text-xl font-bold text-blue-800">{birthdayMembers.length}</div>
                      </div>
                      <div className="bg-purple-50 p-3 rounded text-center">
                        <div className="text-purple-600 font-medium">Total Mesaje</div>
                        <div className="text-xl font-bold text-purple-800">{platformStats.communication?.total_messages || 0}</div>
                      </div>
                      <div className="bg-orange-50 p-3 rounded text-center">
                        <div className="text-orange-600 font-medium">Aplicații Pending</div>
                        <div className="text-xl font-bold text-orange-800">{platformStats.applications?.pending || stats.pending_applications || 0}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        fetchPlatformStats();
                        fetchTodayBirthdays();
                        fetchSurveys();
                        fetchStats();
                      }}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
                    >
                      🔄 Reîmprospătează Statistici
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-indigo-50 p-2 rounded mt-3">
                    <strong>Live:</strong> Statistici din backend actualizate pentru monitorizare.
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">🎨 Personalizare Aplicație</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setShowAppSettingsForm(true)}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Setează Logo & Temă
                    </button>
                    
                    {globalAppSettings.logo_image && (
                      <button
                        onClick={removeLogo}
                        className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
                      >
                        Elimină Logo
                      </button>
                    )}
                    
                    {globalAppSettings.background_image && (
                      <button
                        onClick={removeBackgroundImage}
                        className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
                      >
                        🗑️ Elimină Fundal
                      </button>
                    )}
                    
                    <button
                      onClick={resetTheme}
                      className="w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
                    >
                      Reset Temă Default
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-purple-50 p-2 rounded mt-3">
                    <strong>Previzualizare:</strong> Logo-ul și tema se actualizează instant.
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">💾 Backup & Restore</h3>
                  <div className="space-y-3">
                    <button
                      onClick={createBackup}
                      disabled={isCreatingBackup}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                    >
                      {isCreatingBackup ? '⏳ Creez backup...' : '💾 Creează Backup Complet'}
                    </button>
                    
                    <button
                      onClick={() => setShowBackupOptions(true)}
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      🔄 Restaurează din Backup
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-orange-50 p-2 rounded mt-3">
                    <strong>⚠️ Atenție:</strong> Restaurarea șterge toate datele existente!
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">⚙️ Setări Avansate</h3>
                  <div className="space-y-3">
                    <button
                      onClick={async () => {
                        try {
                          // Încearcă să obțină documentația API reală din backend
                          const response = await fetch(`${API}/docs/endpoints`);
                          
                          if (response.ok) {
                            const apiDocs = await response.json();
                            
                            // Afișează meniul pentru alegerea tipului de documentație
                            const choice = confirm(
                              '📋 DOCUMENTAȚIA API MLNR 1880 DISPONIBILĂ!\n\n' +
                              '🚀 Alegeți formatul dorit:\n\n' +
                              '✅ OK = Documentație Detaliată (JSON)\n' +
                              '❌ Cancel = Swagger UI Interactiv\n\n' +
                              `📊 ${Object.keys(apiDocs.endpoints_by_category).length} categorii disponibile cu ${Object.values(apiDocs.endpoints_by_category).reduce((total, cat) => total + cat.endpoints.length, 0)} endpoint-uri totale`
                            );
                            
                            if (choice) {
                              // Afișează documentația detaliată
                              let docText = '📋 DOCUMENTAȚIA COMPLETĂ API MLNR 1880\n\n';
                              docText += `🔗 ${apiDocs.api_info.title} v${apiDocs.api_info.version}\n`;
                              docText += `📝 ${apiDocs.api_info.description}\n\n`;
                              
                              docText += '🔑 AUTENTIFICARE:\n';
                              docText += `• Type: ${apiDocs.authentication.type}\n`;
                              docText += `• Header: ${apiDocs.authentication.header}\n`;
                              docText += `• Login: ${apiDocs.authentication.login_endpoint}\n\n`;
                              
                              docText += '📚 ENDPOINT-URI PE CATEGORII:\n\n';
                              
                              Object.entries(apiDocs.endpoints_by_category).forEach(([category, info]) => {
                                const categoryEmoji = {
                                  'authentication': '🔐',
                                  'members': '👥',
                                  'applications': '📄',
                                  'messages': '💬',
                                  'notifications': '🔔',
                                  'surveys': '📊',
                                  'grades': '🎓',
                                  'birthdays': '🎂',
                                  'leadership': '👑',
                                  'app_settings': '⚙️',
                                  'backup': '💾',
                                  'statistics': '📈'
                                }[category] || '🔧';
                                
                                docText += `${categoryEmoji} ${category.toUpperCase()} (${info.endpoints.length} endpoints)\n`;
                                docText += `   ${info.description}\n`;
                                
                                info.endpoints.forEach(endpoint => {
                                  const authText = endpoint.auth ? ` [${endpoint.auth}]` : '';
                                  docText += `   • ${endpoint.method} ${endpoint.path}${authText}\n     ${endpoint.description}\n`;
                                });
                                docText += '\n';
                              });
                              
                              docText += '🛠️ GHID DEZVOLTARE:\n';
                              docText += `• Architecture: ${apiDocs.development_guide.architecture}\n`;
                              docText += `• Database: ${apiDocs.development_guide.database}\n`;
                              docText += `• Getting Started: ${apiDocs.development_guide.getting_started}\n\n`;
                              
                              docText += '🎯 EXEMPLE UTILIZARE:\n';
                              Object.entries(apiDocs.development_guide.examples).forEach(([key, example]) => {
                                if (typeof example === 'object') {
                                  docText += `• ${key}:\n`;
                                  Object.entries(example).forEach(([subKey, subExample]) => {
                                    docText += `  - ${subKey}: ${subExample}\n`;
                                  });
                                } else {
                                  docText += `• ${key}: ${example}\n`;
                                }
                              });
                              
                              docText += '\n🌐 LINK-URI UTILE:\n';
                              docText += `• Swagger UI: ${apiDocs.api_info.documentation_urls.swagger_ui}\n`;
                              docText += `• ReDoc: ${apiDocs.api_info.documentation_urls.redoc}\n`;
                              docText += `• OpenAPI JSON: ${apiDocs.api_info.documentation_urls.openapi_json}\n`;
                              
                              // Creează și descarcă fișierul de documentație
                              const blob = new Blob([docText], { type: 'text/plain;charset=utf-8' });
                              const link = document.createElement('a');
                              link.href = URL.createObjectURL(blob);
                              link.download = `MLNR1880_API_Documentation_${new Date().toISOString().slice(0,10)}.txt`;
                              link.click();
                              
                              alert('📋 Documentația completă a fost descărcată!\n\nConține toate detaliile pentru extinderea API-ului MLNR 1880.');
                            } else {
                              // Deschide Swagger UI
                              window.open(apiDocs.api_info.documentation_urls.swagger_ui, '_blank');
                            }
                          } else {
                            throw new Error('Nu s-a putut accesa documentația');
                          }
                        } catch (error) {
                          // Fallback - backend offline
                          const choice = confirm(
                            '📋 DOCUMENTAȚIA API MLNR 1880\n\n' +
                            '🔌 Backend momentan offline, dar documentația este disponibilă!\n\n' +
                            '✅ OK = Deschide Swagger UI\n' +
                            '❌ Cancel = Vezi informații de bază\n\n' +
                            'Porniți backend-ul pentru documentația completă.'
                          );
                          
                          if (choice) {
                            window.open(`${BACKEND_URL}/docs`, '_blank');
                          } else {
                            alert(
                              '📋 MLNR 1880 API - INFORMAȚII DE BAZĂ\n\n' +
                              '🔗 URL-uri importante:\n' +
                                            `• Swagger UI: ${BACKEND_URL}/docs\n` +
              `• ReDoc: ${BACKEND_URL}/redoc\n` +
              `• API JSON: ${BACKEND_URL}/openapi.json\n` +
              `• Documentație: ${API}/docs/endpoints\n\n` +
                              '📚 CATEGORII PRINCIPALE (12 categorii):\n' +
                              '🔐 Autentificare | 👥 Membri | 📄 Aplicații\n' +
                              '💬 Mesaje | 🔔 Notificări | 📊 Sondaje\n' +
                              '🎓 Grade | 🎂 Ziua de naștere | 👑 Conducere\n' +
                              '⚙️ Setări | 💾 Backup | 📈 Statistici\n\n' +
                              '🛠️ Pentru dezvoltatori:\n' +
                              '• FastAPI + MongoDB + JWT\n' +
                              '• 80+ endpoint-uri disponibile\n' +
                              '• Documentație completă Swagger\n' +
                              '• Exemple de cod incluse'
                            );
                          }
                        }
                      }}
                      className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      📋 Info API Endpoints
                    </button>
                    
                    <button
                      onClick={() => fetchPlatformStats()}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      🔄 Reîmprospătează Statistici
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-3">
                    <strong>Info:</strong> Funcții administrative și diagnosticare.
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">📊 Export Rapoarte</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        if (members.length === 0) {
                          alert('Nu există membri pentru export. Încărcați mai întâi datele.');
                          return;
                        }
                        const csvContent = [
                          'Nume,Email,Rol,Status,Grad Masonic,Data Înregistrare',
                          ...members.map(m => `"${m.first_name} ${m.last_name}","${m.email}","${m.role}","${m.is_active ? 'Activ' : 'Inactiv'}","${m.masonic_grade || 'Fără grad'}","${new Date(m.created_at).toLocaleDateString('ro-RO')}"`)
                        ].join('\\n');
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = `membri_mlnr1880_${new Date().toISOString().slice(0,10)}.csv`;
                        link.click();
                        alert(`Export completat! Descărcat fișier cu ${members.length} membri.`);
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      📄 Export Listă Membri CSV ({members.length})
                    </button>
                    
                    <button
                      onClick={() => {
                        if (members.length === 0 && applications.length === 0) {
                          alert('Nu există date pentru raport. Încărcați mai întâi datele.');
                          return;
                        }
                        const reportData = {
                          mlnr1880_raport: {
                            data_generare: new Date().toLocaleString('ro-RO'),
                            versiune: "1.0",
                            organizatie: "Marea Loja Națională Română 1880"
                          },
                          statistici_generale: {
                            total_membri: members.length,
                            membri_activi: members.filter(m => m.is_active).length,
                            membri_inactivi: members.filter(m => !m.is_active).length,
                            total_candidati: members.filter(m => m.role === 'candidate').length,
                            total_administratori: members.filter(m => m.role === 'admin').length
                          },
                          aplicatii: {
                            total: applications.length,
                            pending: applications.filter(a => a.status === 'pending').length,
                            aprobate: applications.filter(a => a.status === 'approved').length,
                            respinse: applications.filter(a => a.status === 'rejected').length
                          },
                          distributie_grade: members.reduce((acc, m) => {
                            const grad = m.masonic_grade || 'Fără grad';
                            acc[grad] = (acc[grad] || 0) + 1;
                            return acc;
                          }, {}),
                          statistici_backend: platformStats
                        };
                        const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = `raport_mlnr1880_${new Date().toISOString().slice(0,10)}.json`;
                        link.click();
                        alert('Raport complet generat și descărcat!');
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      📈 Raport Complet MLNR 1880
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-green-50 p-2 rounded mt-3">
                    <strong>Export:</strong> Funcționează cu datele încărcate din backend.
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">🔔 Notificări Auto</h3>
                  <div className="space-y-3">
                    <button
                      onClick={async () => {
                        if (birthdayMembers.length === 0) {
                          alert('Nu sunt membri cu ziua de naștere astăzi.');
                          return;
                        }
                        let successCount = 0;
                        for (const member of birthdayMembers) {
                          try {
                            await sendBirthdayNotification(member.id);
                            successCount++;
                          } catch (error) {
                            console.error(`Eroare trimitere notificare pentru ${member.first_name}:`, error);
                          }
                        }
                        alert(`✅ Trimise ${successCount} din ${birthdayMembers.length} notificări de ziua de naștere!`);
                      }}
                      disabled={birthdayMembers.length === 0}
                      className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                    >
                      🎂 Trimite Notificări Ziua de Naștere ({birthdayMembers.length})
                    </button>
                    
                    <button
                      onClick={() => {
                        if (members.length === 0) {
                          alert('Încărcați mai întâi lista de membri.');
                          return;
                        }
                        const inactiveMembers = members.filter(m => !m.is_active && m.role !== 'admin');
                        if (inactiveMembers.length > 0) {
                          const membersList = inactiveMembers.map(m => `• ${m.first_name} ${m.last_name} (${m.email})`).join('\\n');
                          alert(`⚠️ MEMBRI INACTIVI GĂSIȚI (${inactiveMembers.length}): \\n\\n${membersList}\\n\\nVă recomandăm să îi contactați pentru reactivare.`);
                        } else {
                          alert('🎉 Toți membrii sunt activi! Excelent!');
                        }
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      ⚠️ Verifică Membri Inactivi
                    </button>

                    <button
                      onClick={() => {
                        if (applications.length === 0) {
                          alert('Nu există aplicații de procesat.');
                          return;
                        }
                        const pendingApps = applications.filter(a => a.status === 'pending');
                        if (pendingApps.length > 0) {
                          alert(`📋 APLICAȚII PENDING: ${pendingApps.length}\\n\\nAccesați tab-ul "Aplicații" pentru a le procesa.`);
                        } else {
                          alert('✅ Toate aplicațiile au fost procesate!');
                        }
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      📋 Verifică Aplicații Pending
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 bg-yellow-50 p-2 rounded mt-3">
                    <strong>Auto:</strong> Verificări și notificări pentru administrarea eficientă.
                  </div>
                </div>
              </div>

              {globalAppSettings.logo_image && (
                <div className="mt-6 bg-gray-50 border rounded-lg p-4">
                  <h4 className="text-md font-semibold mb-3">🖼️ Preview Logo Curent</h4>
                  <div className="flex items-center space-x-4">
                    <img 
                      src={`data:image/png;base64,${globalAppSettings.logo_image}`}
                      alt="Logo aplicație"
                      className="w-16 h-16 object-contain border rounded-lg bg-white p-2"
                    />
                    <div className="text-sm text-gray-600">
                      <p>Logo-ul curent este activ în header</p>
                      <p className="text-xs">Format: PNG/JPG, max 2MB recomandat</p>
                    </div>
                  </div>
                </div>
              )}

              {globalAppSettings.background_image && (
                <div className="mt-6 bg-gray-50 border rounded-lg p-4">
                  <h4 className="text-md font-semibold mb-3">🌄 Preview Fundal Curent</h4>
                  <div className="flex items-center space-x-4">
                    <img 
                      src={`data:image/png;base64,${globalAppSettings.background_image}`}
                      alt="Fundal aplicație"
                      className="w-20 h-12 object-cover border rounded-lg"
                    />
                    <div className="text-sm text-gray-600">
                      <p>Imaginea de fundal este activă în aplicație</p>
                      <p className="text-xs">Format: PNG/JPG, 1920x1080px+ recomandat</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 bg-gray-50 border rounded-lg p-4">
                <h4 className="text-md font-semibold mb-3">🎨 Preview Temă Curentă</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded border"
                      style={{backgroundColor: globalAppSettings.theme_colors.primary}}
                    ></div>
                    <p className="text-xs mt-1">Primary: {globalAppSettings.theme_colors.primary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded border"
                      style={{backgroundColor: globalAppSettings.theme_colors.secondary}}
                    ></div>
                    <p className="text-xs mt-1">Secondary: {globalAppSettings.theme_colors.secondary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-8 rounded border"
                      style={{backgroundColor: globalAppSettings.theme_colors.accent}}
                    ></div>
                    <p className="text-xs mt-1">Accent: {globalAppSettings.theme_colors.accent}</p>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <div 
                    className="w-full h-8 rounded border"
                    style={{background: globalAppSettings.header_background}}
                  ></div>
                  <p className="text-xs mt-1">Header Background</p>
                </div>
                              </div>
              </div>
            )}

        {/* Modal Creare Sondaj */}
      {showSurveyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">📊 Creează Sondaj Nou</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlul Sondajului</label>
                <input
                  type="text"
                  value={newSurvey.title}
                  onChange={(e) => setNewSurvey({...newSurvey, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Ce părere aveți despre orarul ședințelor?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrierea Sondajului</label>
                <textarea
                  value={newSurvey.description}
                  onChange={(e) => setNewSurvey({...newSurvey, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descriere detaliată a sondajului..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opțiuni de Răspuns</label>
                {newSurvey.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...newSurvey.options];
                        newOptions[index] = e.target.value;
                        setNewSurvey({...newSurvey, options: newOptions});
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Opțiunea ${index + 1}`}
                    />
                    {newSurvey.options.length > 2 && (
                      <button
                        onClick={() => {
                          const newOptions = newSurvey.options.filter((_, i) => i !== index);
                          setNewSurvey({...newSurvey, options: newOptions});
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setNewSurvey({...newSurvey, options: [...newSurvey.options, '']})}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  ➕ Adaugă opțiune
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newSurvey.is_anonymous}
                      onChange={(e) => setNewSurvey({...newSurvey, is_anonymous: e.target.checked})}
                      className="mr-2"
                    />
                    Sondaj anonim
                  </label>
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newSurvey.allows_multiple_votes}
                      onChange={(e) => setNewSurvey({...newSurvey, allows_multiple_votes: e.target.checked})}
                      className="mr-2"
                    />
                    Permite voturi multiple
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de expirare (opțional)</label>
                <input
                  type="datetime-local"
                  value={newSurvey.end_date}
                  onChange={(e) => setNewSurvey({...newSurvey, end_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowSurveyForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Anulează
              </button>
              <button
                onClick={createSurvey}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                Creează Sondaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rezultate Sondaj */}
      {showSurveyResults && surveyResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">📈 Rezultate Sondaj: {surveyResults.survey.title}</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">{surveyResults.survey.description}</p>
              <p className="text-sm text-gray-500">Total voturi: {surveyResults.total_votes}</p>
            </div>

            <div className="space-y-4 mb-6">
              {Object.entries(surveyResults.results).map(([option, count]) => {
                const percentage = surveyResults.total_votes > 0 ? Math.round((count / surveyResults.total_votes) * 100) : 0;
                return (
                  <div key={option} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{option}</span>
                      <span className="text-sm text-gray-600">{count} voturi ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{width: `${percentage}%`}}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {!surveyResults.survey.is_anonymous && surveyResults.voters.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">👥 Votanți ({surveyResults.voters.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {surveyResults.voters.map((voter, index) => (
                    <div key={index} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                      <span>{voter.name}</span>
                      <div className="text-xs text-gray-500">
                        <span className="mr-2">{new Date(voter.voted_at).toLocaleDateString('ro-RO')}</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {voter.selected_options.join(', ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowSurveyResults(null);
                  setSurveyResults(null);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Notificare Avansată */}
      {showAdvancedNotificationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">📢 Trimite Notificare Avansată</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlul Notificării</label>
                <input
                  type="text"
                  value={newAdvancedNotification.title}
                  onChange={(e) => setNewAdvancedNotification({...newAdvancedNotification, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex: Ședință extraordinară"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conținutul Notificării</label>
                <textarea
                  value={newAdvancedNotification.content}
                  onChange={(e) => setNewAdvancedNotification({...newAdvancedNotification, content: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Conținutul complet al notificării..."
                />
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">🎯 Selectare Destinatari</h4>
                
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newAdvancedNotification.send_to_all_grades}
                      onChange={(e) => setNewAdvancedNotification({
                        ...newAdvancedNotification, 
                        send_to_all_grades: e.target.checked,
                        send_by_names: false
                      })}
                      className="mr-2"
                    />
                    📊 Trimite către toate gradele (1° - 33°)
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newAdvancedNotification.send_by_names}
                      onChange={(e) => setNewAdvancedNotification({
                        ...newAdvancedNotification, 
                        send_by_names: e.target.checked,
                        send_to_all_grades: false
                      })}
                      className="mr-2"
                    />
                    👥 Trimite pe baza numelor specifice
                  </label>
                </div>

                {!newAdvancedNotification.send_to_all_grades && !newAdvancedNotification.send_by_names && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Selectează Grade Specifice</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto border p-3 rounded">
                      {Object.entries(gradeHierarchy).map(([gradeKey, gradeInfo]) => (
                        <label key={gradeKey} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={newAdvancedNotification.target_grades.includes(gradeKey)}
                            onChange={(e) => {
                              const grades = e.target.checked 
                                ? [...newAdvancedNotification.target_grades, gradeKey]
                                : newAdvancedNotification.target_grades.filter(g => g !== gradeKey);
                              setNewAdvancedNotification({...newAdvancedNotification, target_grades: grades});
                            }}
                            className="mr-1"
                          />
                          <span className="text-xs">{gradeInfo.display_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {newAdvancedNotification.send_by_names && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nume Complete (câte unul pe linie)
                    </label>
                    <textarea
                      value={newAdvancedNotification.target_names.join('\n')}
                      onChange={(e) => setNewAdvancedNotification({
                        ...newAdvancedNotification, 
                        target_names: e.target.value.split('\n').filter(name => name.trim())
                      })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Ion Popescu&#10;Maria Ionescu&#10;Gheorghe Vasilescu"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Introduceți numele complete exact cum apar în sistem, câte unul pe linie.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipul Notificării</label>
                <select
                  value={newAdvancedNotification.notification_type}
                  onChange={(e) => setNewAdvancedNotification({...newAdvancedNotification, notification_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="general">📋 Generală</option>
                  <option value="grade_notification">🎖️ Notificare Grad</option>
                  <option value="birthday">🎂 Zi de naștere</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAdvancedNotificationForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Anulează
              </button>
              <button
                onClick={sendAdvancedNotification}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                📨 Trimite Notificare
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App Settings Modal */}
      {showAppSettingsForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">🎨 Personalizare Logo & Temă MLNR 1880</h3>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const newSettings = {};
              
              // Logo upload
              const logoFile = formData.get('logo');
              if (logoFile && logoFile.size > 0) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  newSettings.logo_image = e.target.result.split(',')[1]; // Remove data:image/...;base64,
                  updateAppSettings(newSettings);
                };
                reader.readAsDataURL(logoFile);
                return;
              }
              
              // Background image upload
              const backgroundFile = formData.get('background_image');
              if (backgroundFile && backgroundFile.size > 0) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  newSettings.background_image = e.target.result.split(',')[1]; // Remove data:image/...;base64,
                  updateAppSettings(newSettings);
                };
                reader.readAsDataURL(backgroundFile);
                return;
              }
              
              // Theme colors
              const primaryColor = formData.get('primary_color');
              const secondaryColor = formData.get('secondary_color');
              const accentColor = formData.get('accent_color');
              const headerBackground = formData.get('header_background');
              
              if (primaryColor || secondaryColor || accentColor || headerBackground) {
                                 if (primaryColor || secondaryColor || accentColor) {
                   newSettings.theme_colors = {
                     primary: primaryColor || globalAppSettings.theme_colors.primary,
                     secondary: secondaryColor || globalAppSettings.theme_colors.secondary,
                     accent: accentColor || globalAppSettings.theme_colors.accent
                   };
                 }
                if (headerBackground) {
                  newSettings.header_background = headerBackground;
                }
                updateAppSettings(newSettings);
              }
            }}>
              <div className="space-y-6">
                {/* Logo Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">🖼️ Logo Aplicație</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Încarcă Logo Nou (PNG/JPG)
                      </label>
                      <input
                        type="file"
                        name="logo"
                        accept="image/png,image/jpeg,image/jpg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Recomandare: 200x200px, max 2MB. Fundal transparent pentru cel mai bun rezultat.
                      </p>
                    </div>
                    
                                         {globalAppSettings.logo_image && (
                       <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                         <img 
                           src={`data:image/png;base64,${globalAppSettings.logo_image}`}
                           alt="Logo curent"
                           className="w-12 h-12 object-contain border rounded bg-white p-1"
                         />
                         <span className="text-sm text-gray-600">Logo curent activ</span>
                       </div>
                     )}
                  </div>
                </div>

                {/* Theme Colors Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">🎨 Culori Temă</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Culoare Primară
                      </label>
                      <input
                        type="color"
                        name="primary_color"
                        defaultValue={globalAppSettings.theme_colors.primary}
                        className="w-full h-10 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Culoare Secundară
                      </label>
                      <input
                        type="color"
                        name="secondary_color"
                        defaultValue={globalAppSettings.theme_colors.secondary}
                        className="w-full h-10 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Culoare Accent
                      </label>
                      <input
                        type="color"
                        name="accent_color"
                        defaultValue={globalAppSettings.theme_colors.accent}
                        className="w-full h-10 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Header Background Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">🌈 Fundal Header</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CSS Background (gradient sau culoare)
                    </label>
                    <input
                      type="text"
                      name="header_background"
                      defaultValue={globalAppSettings.header_background}
                      placeholder="Ex: linear-gradient(135deg, #1e3a8a 0%, #dc2626 50%, #1e3a8a 100%)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Puteți folosi gradient-uri CSS sau culori simple (#ffffff sau red)
                    </p>
                  </div>
                </div>

                {/* Background Image Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">🌄 Imagine Fundal Aplicație</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Încarcă Imagine de Fundal (PNG/JPG)
                      </label>
                      <input
                        type="file"
                        name="background_image"
                        accept="image/png,image/jpeg,image/jpg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Recomandare: 1920x1080px sau mai mare, max 5MB. Pentru cel mai bun efect vizual.
                      </p>
                    </div>
                    
                    {globalAppSettings.background_image && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                          <img 
                            src={`data:image/png;base64,${globalAppSettings.background_image}`}
                            alt="Fundal curent"
                            className="w-16 h-12 object-cover border rounded bg-white"
                          />
                          <span className="text-sm text-gray-600">Imagine de fundal activă</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeBackgroundImage}
                          className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-sm"
                        >
                          🗑️ Elimină Imaginea de Fundal
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAppSettingsForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md"
                >
                  Actualizează Setările
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">🔒 Schimbare Parolă Admin</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parola Curentă</label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parola Nouă</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmă Parola Nouă</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 my-4">
              <p className="text-yellow-800 text-xs">
                <strong>Important:</strong> Parola nouă trebuie să aibă minim 12 caractere și să conțină litere mari, mici, cifre și simboluri.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordData({current_password: '', new_password: '', confirm_password: ''});
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={changePassword}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                Schimbă Parola
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member Profile Modal */}
      {showMemberProfileModal && selectedMemberProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-bold text-gray-800">👤 Profil Complet Membru</h3>
              <button
                onClick={() => setShowMemberProfileModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Photo and Basic Info */}
              <div className="space-y-4">
                <div className="text-center">
                  {selectedMemberProfile.profile_photo ? (
                    <img 
                      src={selectedMemberProfile.profile_photo} 
                      alt={`${selectedMemberProfile.first_name} ${selectedMemberProfile.last_name}`}
                      className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-gray-300 shadow-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gray-300 rounded-full flex items-center justify-center mx-auto border-4 border-gray-300 shadow-lg">
                      <span className="text-gray-600 font-bold text-2xl">
                        {selectedMemberProfile.first_name[0]}{selectedMemberProfile.last_name[0]}
                      </span>
                    </div>
                  )}
                  <h4 className="text-lg font-semibold mt-3 text-gray-800">
                    {selectedMemberProfile.first_name} {selectedMemberProfile.last_name}
                  </h4>
                  <p className="text-sm text-gray-600">{selectedMemberProfile.email}</p>
                </div>

                {/* Status Badge */}
                <div className="text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    selectedMemberProfile.role === 'admin' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedMemberProfile.role === 'admin' ? '👑 Administrator' : '🔵 Membru'}
                  </span>
                </div>

                {/* CV Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-800 mb-3">📄 CV Membru</h5>
                  {selectedMemberProfile.has_cv ? (
                    <div className="space-y-2">
                      <p className="text-sm text-green-700">✅ CV încărcat</p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => downloadMemberCV(selectedMemberProfile.id)}
                          className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                        >
                          📥 Descarcă CV
                        </button>
                        <button
                          onClick={() => deleteMemberCV(selectedMemberProfile.id)}
                          className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                        >
                          🗑️ Șterge CV
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">❌ CV nu a fost încărcat</p>
                      <button
                        onClick={() => setShowCVUploadModal(true)}
                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                      >
                        📤 Încarcă CV
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Columns - Detailed Information */}
              <div className="lg:col-span-2 space-y-6">
                {/* Masonic Grade Section */}
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h5 className="font-semibold text-purple-800 mb-3">🏛️ Informații Masonice</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Grad Masonic:</p>
                      <p className="font-semibold text-purple-700">
                        {selectedMemberProfile.grade_display_name}
                        {selectedMemberProfile.grade_level > 0 && (
                          <span className="ml-2 text-sm text-gray-500">({selectedMemberProfile.grade_level}°)</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Categorie:</p>
                      <p className="font-semibold text-purple-700">{selectedMemberProfile.grade_category}</p>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h5 className="font-semibold text-blue-800 mb-3">👤 Informații Personale</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nume Complet:</p>
                      <p className="font-semibold text-blue-700">
                        {selectedMemberProfile.first_name} {selectedMemberProfile.last_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email:</p>
                      <p className="font-semibold text-blue-700">{selectedMemberProfile.email}</p>
                    </div>
                    {selectedMemberProfile.birth_date && (
                      <div>
                        <p className="text-sm text-gray-600">Data Nașterii:</p>
                        <p className="font-semibold text-blue-700">
                          {new Date(selectedMemberProfile.birth_date).toLocaleDateString('ro-RO')}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600">Membru din:</p>
                      <p className="font-semibold text-blue-700">{selectedMemberProfile.member_since}</p>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h5 className="font-semibold text-green-800 mb-3">⚙️ Status Cont</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Status:</p>
                      <p className={`font-semibold ${
                        selectedMemberProfile.is_active ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {selectedMemberProfile.is_active ? '✅ Activ' : '❌ Inactiv'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tip Cont:</p>
                      <p className="font-semibold text-green-700">
                        {selectedMemberProfile.role === 'admin' ? 'Administrator' : 'Membru'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CV Upload Modal */}
      {showCVUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">📤 Încărcare CV Membru</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Încărcați CV-ul pentru: <strong>{selectedMemberProfile?.first_name} {selectedMemberProfile?.last_name}</strong>
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleCVUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Acceptă doar fișiere PDF, max 5MB
                </p>
              </div>
              
              {cvFile && (
                <div className="bg-blue-50 p-3 rounded border">
                  <p className="text-sm text-blue-800">✅ Fișier CV selectat și gata pentru încărcare</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowCVUploadModal(false);
                  setCvFile(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={uploadMemberCV}
                disabled={!cvFile}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:bg-gray-400"
              >
                Încarcă CV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instruction Upload Modal */}
      {showInstructionUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">📚 Adaugă Instrucțiune Grad</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grad Masonic</label>
                <select
                  value={newInstruction.grade}
                  onChange={(e) => setNewInstruction({...newInstruction, grade: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selectați gradul...</option>
                  {Object.entries(gradeHierarchy).map(([category, grades]) => (
                    <optgroup key={category} label={category}>
                      {grades.map((grade) => (
                        <option key={grade.grade} value={grade.grade}>
                          {grade.level}° - {grade.display_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlu Instrucțiune</label>
                <input
                  type="text"
                  value={newInstruction.title}
                  onChange={(e) => setNewInstruction({...newInstruction, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
                <textarea
                  value={newInstruction.description}
                  onChange={(e) => setNewInstruction({...newInstruction, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fișier PDF</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        setNewInstruction({...newInstruction, pdf_content: e.target.result});
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowInstructionUpload(false);
                  setNewInstruction({grade: '', title: '', description: '', pdf_content: ''});
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/grade-instructions`, newInstruction);
                    alert('Instrucțiunea a fost adăugată cu succes!');
                    setShowInstructionUpload(false);
                    setNewInstruction({grade: '', title: '', description: '', pdf_content: ''});
                    // Refresh instructions list
                  } catch (error) {
                    alert('Eroare: ' + (error.response?.data?.detail || 'Unknown error'));
                  }
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                Adaugă Instrucțiune
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Edit Instruction Modal */}
      {showEditInstruction && editingInstruction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">✏️ Editare Instrucțiune Grad</h3>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-gray-700">
                <strong>Editare instrucțiune:</strong> {editingInstruction.title}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Grad:</strong> {editingInstruction.grade_display_name}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grad Masonic</label>
                <select
                  value={newInstruction.grade}
                  onChange={(e) => setNewInstruction({...newInstruction, grade: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{editingInstruction.grade_display_name}</option>
                  <option value="1">1° - Ucenic</option>
                  <option value="2">2° - Calfă</option>
                  <option value="3">3° - Maestru</option>
                  <option value="4">4° - Maestru Secret</option>
                  <option value="5">5° - Maestru Perfect</option>
                  <option value="6">6° - Secretar Intim</option>
                  <option value="7">7° - Prevot și Judecător</option>
                  <option value="8">8° - Intendent al Construcțiilor</option>
                  <option value="9">9° - Maestru Ales de Nouă</option>
                  <option value="10">10° - Ilustru Ales de Cincisprezece</option>
                  <option value="11">11° - Sublim Cavaler Ales</option>
                  <option value="12">12° - Marele Maestru Arhitect</option>
                  <option value="13">13° - Cavaler al Arcului Regal</option>
                  <option value="14">14° - Mare Ales Perfect și Sublim</option>
                  <option value="15">15° - Cavaler al Răsăritului</option>
                  <option value="16">16° - Prinț al Ierusalimului</option>
                  <option value="17">17° - Cavaler al Răsăritului și Apusului</option>
                  <option value="18">18° - Cavaler al Rozei-Crucii</option>
                  <option value="19">19° - Mare Pontifice</option>
                  <option value="20">20° - Venerabil Mare Maestru</option>
                  <option value="21">21° - Noachit sau Cavaler Prusac</option>
                  <option value="22">22° - Cavaler al Hachei Regale</option>
                  <option value="23">23° - Șef al Tabernacolului</option>
                  <option value="24">24° - Prinț al Tabernacolului</option>
                  <option value="25">25° - Cavaler al Șarpelui de Aramă</option>
                  <option value="26">26° - Prinț al Milostivirii</option>
                  <option value="27">27° - Cavaler Comandor al Templului</option>
                  <option value="28">28° - Cavaler al Soarelui</option>
                  <option value="29">29° - Mare Cavaler Scoțian</option>
                  <option value="30">30° - Cavaler Kadosh</option>
                  <option value="31">31° - Mare Inspector Inchizitor</option>
                  <option value="32">32° - Sublim Prinț al Secretului Regal</option>
                  <option value="33">33° - Sovran Mare Inspector General</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlu Instrucțiune *</label>
                <input
                  type="text"
                  required
                  value={newInstruction.title}
                  onChange={(e) => setNewInstruction({...newInstruction, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Instrucțiuni pentru gradul 3°"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descriere *</label>
                <textarea
                  required
                  value={newInstruction.description}
                  onChange={(e) => setNewInstruction({...newInstruction, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrierea instrucțiunii..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PDF Instrucțiune (opțional - doar dacă doriți să înlocuiți)</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setNewInstruction({...newInstruction, pdf_content: event.target.result});
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lăsați gol dacă nu doriți să schimbați PDF-ul existent
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditInstruction(false);
                  setEditingInstruction(null);
                  setNewInstruction({grade: '', title: '', description: '', pdf_content: ''});
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={updateInstruction}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-md"
              >
                Actualizează Instrucțiunea
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member Password Reset Modal */}
      {showMemberPasswordReset && selectedMemberForPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">🔑 Resetare Parolă Membru</h3>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-gray-700">
                <strong>Membru:</strong> {selectedMemberForPasswordReset.first_name} {selectedMemberForPasswordReset.last_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Email:</strong> {selectedMemberForPasswordReset.email}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Rol:</strong> {selectedMemberForPasswordReset.role === 'member' ? 'Frate' : 'Candidat'}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parolă Nouă</label>
                <input
                  type="password"
                  value={memberPasswordResetData.new_password}
                  onChange={(e) => setMemberPasswordResetData({
                    ...memberPasswordResetData, 
                    new_password: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Introduceți parola nouă"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmă Parola Nouă</label>
                <input
                  type="password"
                  value={memberPasswordResetData.confirm_password}
                  onChange={(e) => setMemberPasswordResetData({
                    ...memberPasswordResetData, 
                    confirm_password: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Confirmați parola nouă"
                />
              </div>
            </div>
            
            <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
              <p className="text-red-800 text-xs">
                <strong>⚠️ Atenție:</strong> După resetarea parolei, membrul va trebui să se reconecteze cu noua parolă. 
                Informați-l despre noua parolă prin canal sigur (telefon, întâlnire personală).
              </p>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMemberPasswordReset(false);
                  setSelectedMemberForPasswordReset(null);
                  setMemberPasswordResetData({
                    new_password: '',
                    confirm_password: ''
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={resetMemberPassword}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-md"
              >
                🔑 Resetează Parola
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Member Modal */}
      {showAddMemberForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingMember ? 'Editare Profil' : 'Adăugare Frate Nou în MLNR 1880'}
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prenume *</label>
                  <input
                    type="text"
                    required
                    value={newMemberData.first_name}
                    onChange={(e) => setNewMemberData({...newMemberData, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nume *</label>
                  <input
                    type="text"
                    required
                    value={newMemberData.last_name}
                    onChange={(e) => setNewMemberData({...newMemberData, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={newMemberData.email}
                  onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {!editingMember && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parolă *</label>
                  <input
                    type="password"
                    required
                    value={newMemberData.password}
                    onChange={(e) => setNewMemberData({...newMemberData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data nașterii</label>
                <input
                  type="date"
                  value={newMemberData.birth_date}
                  onChange={(e) => setNewMemberData({...newMemberData, birth_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol în MLNR 1880</label>
                <select
                  value={newMemberData.role}
                  onChange={(e) => setNewMemberData({...newMemberData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="candidate">Candidat</option>
                  <option value="member">Frate</option>
                  <option value="admin">Maestru Venerat</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddMemberForm(false);
                  setEditingMember(null);
                  setNewMemberData({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    birth_date: '',
                    role: 'member'
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={editingMember ? updateMember : addMember}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                {editingMember ? 'Actualizează' : 'Adaugă în MLNR 1880'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">🔐 Schimbare Parolă Maestru Venerat</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parola curentă *</label>
                <input
                  type="password"
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parolă nouă *</label>
                <input
                  type="password"
                  required
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Min. 12 caractere, cu litere mari, mici, cifre, caractere speciale"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmă parola nouă *</label>
                <input
                  type="password"
                  required
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-yellow-800 text-xs">
                  <strong>Cerințe securitate:</strong> Parola trebuie să conțină minim 12 caractere, 
                  cu cel puțin o literă mare, o literă mică, o cifră și un caracter special (!@#$%^&*).
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={changePassword}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                🔐 Schimbă Parola
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal Notification Modal */}
      {showGradeNotificationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">📢 Trimite Notificare Internă MLNR 1880</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titlu Notificare</label>
                <input
                  type="text"
                  value={newGradeNotification.title}
                  onChange={(e) => setNewGradeNotification({...newGradeNotification, title: e.target.value})}
                  placeholder="Ex: Anunț important pentru frații..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conținut Notificare</label>
                <textarea
                  value={newGradeNotification.content}
                  onChange={(e) => setNewGradeNotification({...newGradeNotification, content: e.target.value})}
                  rows={4}
                  placeholder="Conținutul notificării pentru frații selectați..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grade Țintă</label>
                
                {/* Opțiune pentru toate gradele */}
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="flex items-center font-medium text-blue-800">
                    <input
                      type="checkbox"
                      checked={newGradeNotification.send_to_all_grades || false}
                      onChange={(e) => {
                        setNewGradeNotification({
                          ...newGradeNotification,
                          send_to_all_grades: e.target.checked,
                          target_grades: e.target.checked ? [] : newGradeNotification.target_grades
                        });
                      }}
                      className="mr-3 h-4 w-4 text-blue-600"
                    />
                    🏛️ TOATE GRADELE (1° - 33°) - Trimite către toți membrii
                  </label>
                  <p className="text-xs text-blue-600 mt-1 ml-7">
                    Selectați pentru a trimite notificarea către TOȚI membrii din MLNR 1880
                  </p>
                </div>
                
                {/* Selecție individuală grade */}
                {!newGradeNotification.send_to_all_grades && (
                  <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3">
                    <p className="text-sm text-gray-600 mb-3">Sau selectați grade specifice:</p>
                    {Object.entries(gradeHierarchy).map(([category, grades]) => (
                      <div key={category} className="mb-4">
                        <div className="font-medium text-sm text-purple-700 mb-2 border-b border-purple-200 pb-1">
                          {category}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {grades.map((grade) => (
                            <label key={grade.grade} className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={newGradeNotification.target_grades.includes(grade.grade)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewGradeNotification({
                                      ...newGradeNotification,
                                      target_grades: [...newGradeNotification.target_grades, grade.grade]
                                    });
                                  } else {
                                    setNewGradeNotification({
                                      ...newGradeNotification,
                                      target_grades: newGradeNotification.target_grades.filter(g => g !== grade.grade)
                                    });
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-purple-800 font-medium">{grade.level}°</span>
                              <span className="ml-1">{grade.display_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Notificare</label>
                <select
                  value={newGradeNotification.notification_type}
                  onChange={(e) => setNewGradeNotification({...newGradeNotification, notification_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">📢 General</option>
                  <option value="grade_notification">🏛️ Anunț Grad</option>
                  <option value="birthday">🎂 Ziua de Naștere</option>
                </select>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded p-3 my-4">
              <p className="text-green-800 text-xs">
                <strong>📱 Notificare Internă:</strong> Aceasta va fi trimisă direct în aplicația MLNR 1880. 
                Membrii o vor vedea în tab-ul "Notificări" când se vor autentifica.
                {newGradeNotification.send_to_all_grades && (
                  <span className="block mt-1 font-medium">
                    🎯 Țintă: TOȚI MEMBRII (1° - 33°)
                  </span>
                )}
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGradeNotificationForm(false);
                  setNewGradeNotification({
                    title: '',
                    content: '',
                    target_grades: [],
                    notification_type: 'general',
                    send_to_all_grades: false
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={async () => {
                  if (!newGradeNotification.title || !newGradeNotification.content) {
                    alert('Vă rugăm să completați titlul și conținutul notificării');
                    return;
                  }
                  
                  if (!newGradeNotification.send_to_all_grades && newGradeNotification.target_grades.length === 0) {
                    alert('Vă rugăm să selectați cel puțin un grad sau opțiunea "Toate gradele"');
                    return;
                  }
                  
                  try {
                    const response = await axios.post(`${API}/admin/internal-notifications`, newGradeNotification);
                    alert(`✅ Succes! ${response.data.message}\n🎯 Țintă: ${response.data.target_grades}\n👥 Destinatari: ${response.data.total_recipients}`);
                    setShowGradeNotificationForm(false);
                    setNewGradeNotification({
                      title: '',
                      content: '',
                      target_grades: [],
                      notification_type: 'general',
                      send_to_all_grades: false
                    });
                  } catch (error) {
                    alert('❌ Eroare: ' + (error.response?.data?.detail || 'Unknown error'));
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md"
              >
                📢 Trimite Notificare Internă
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pentru selectarea gradului masonic - Îmbunătățit */}
      {showGradeSelector.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                🏛️ Atribuire Grad Masonic MLNR 1880
              </h3>
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800">
                  <strong>Membru:</strong> F∴ {showGradeSelector.member?.first_name} {showGradeSelector.member?.last_name}
                </p>
                <p className="text-blue-700 text-sm mt-1">
                  <strong>Grad actual:</strong> {showGradeSelector.member?.masonic_grade ? 
                    getGradeDisplayName(showGradeSelector.member.masonic_grade) : 
                    'Fără grad atribuit'}
                </p>
              </div>
            </div>
            
            <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">⚠️</div>
                <div className="ml-3">
                  <p className="text-yellow-800 text-sm">
                    <strong>Atenție:</strong> Gradul masonic atribuit va fi permanent înregistrat în profilul membrului. 
                    Selectați cu atenție gradul corespunzător conform ierarhiei celor 33 de grade ale MLNR 1880.
                  </p>
                  <p className="text-yellow-700 text-xs mt-1">
                    Această acțiune va fi înregistrată în jurnalul de audit al Lojei.
                  </p>
                </div>
              </div>
            </div>

            {/* Selector rapid pentru gradele comune */}
            <div className="mb-6 bg-gray-50 border rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3">🚀 Selecție Rapidă - Grade Comune</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  {grade: '1', name: 'Ucenic', level: '1°', color: 'bg-blue-100 border-blue-300 text-blue-800'},
                  {grade: '2', name: 'Calfă', level: '2°', color: 'bg-green-100 border-green-300 text-green-800'},
                  {grade: '3', name: 'Maestru', level: '3°', color: 'bg-purple-100 border-purple-300 text-purple-800'},
                  {grade: '18', name: 'Cavaler al Rozei-Crucii', level: '18°', color: 'bg-red-100 border-red-300 text-red-800'},
                  {grade: '30', name: 'Cavaler Kadosh', level: '30°', color: 'bg-indigo-100 border-indigo-300 text-indigo-800'},
                  {grade: '33', name: 'Inspector General', level: '33°', color: 'bg-yellow-100 border-yellow-300 text-yellow-800'}
                ].map(quickGrade => (
                  <button
                    key={quickGrade.grade}
                    onClick={() => setSelectedGrade(quickGrade.grade)}
                    className={`p-2 border-2 rounded-lg text-sm font-medium transition-all hover:shadow-md ${
                      selectedGrade === quickGrade.grade 
                        ? 'ring-2 ring-purple-500 ' + quickGrade.color
                        : 'border-gray-200 hover:border-gray-300 ' + quickGrade.color
                    }`}
                  >
                    <div className="font-bold">{quickGrade.level}</div>
                    <div className="text-xs">{quickGrade.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selecție completă ierarhică */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(gradeHierarchy).map(([category, grades]) => (
                <div key={category} className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <h4 className="font-bold text-lg mb-3 pb-2 border-b border-gray-200 flex items-center">
                    {category === 'Simbolic' && <span className="mr-2">🔺</span>} 
                    {category === 'Capitular' && <span className="mr-2">🔶</span>} 
                    {category === 'Filosofic' && <span className="mr-2">⭐</span>} 
                    {category === 'Administrativ' && <span className="mr-2">👑</span>}
                    <span className="text-blue-800">{category}</span>
                    <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                      {grades.length} grade
                    </span>
                  </h4>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {grades.map((grade) => {
                      const memberCount = members.filter(m => m.masonic_grade === grade.grade).length;
                      return (
                        <label 
                          key={grade.grade} 
                          className={`flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors border ${
                            selectedGrade === grade.grade 
                              ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-500' 
                              : 'border-transparent'
                          }`}
                        >
                          <input
                            type="radio"
                            name="selectedGrade"
                            value={grade.grade}
                            checked={selectedGrade === grade.grade}
                            onChange={(e) => setSelectedGrade(e.target.value)}
                            className="text-purple-600 focus:ring-purple-500 h-4 w-4"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-bold text-purple-800 text-sm">{grade.level}°</span>
                                <span className="ml-2 text-sm font-medium text-gray-800">{grade.display_name}</span>
                              </div>
                              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                {memberCount > 0 ? `${memberCount} F∴` : '0 F∴'}
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Informații despre gradul selectat */}
            {selectedGrade && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ Grad Selectat</h4>
                <div className="text-green-700">
                  {(() => {
                    const gradeInfo = Object.values(gradeHierarchy).flat().find(g => g.grade === selectedGrade);
                    return gradeInfo ? (
                      <div>
                        <p className="font-medium">{gradeInfo.level}° - {gradeInfo.display_name}</p>
                        <p className="text-sm">
                          Categorie: {Object.keys(gradeHierarchy).find(cat => 
                            gradeHierarchy[cat].some(g => g.grade === selectedGrade)
                          )}
                        </p>
                        <p className="text-xs mt-1">
                          Membri actuali cu acest grad: {members.filter(m => m.masonic_grade === selectedGrade).length}
                        </p>
                      </div>
                    ) : 'Informații grad indisponibile';
                  })()}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowGradeSelector({show: false, member: null});
                  setSelectedGrade('');
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                ❌ Anulează
              </button>
              <button
                onClick={() => {
                  if (!selectedGrade) {
                    alert('Vă rugăm să selectați un grad masonic');
                    return;
                  }
                  if (window.confirm(`Sigur doriți să atribuiți gradul ${(() => {
                    const gradeInfo = Object.values(gradeHierarchy).flat().find(g => g.grade === selectedGrade);
                    return gradeInfo ? `${gradeInfo.level}° - ${gradeInfo.display_name}` : selectedGrade;
                  })()} pentru F∴ ${showGradeSelector.member?.first_name} ${showGradeSelector.member?.last_name}?`)) {
                    assignGrade(showGradeSelector.member.id, selectedGrade);
                  }
                }}
                className={`font-medium py-2 px-6 rounded-md transition-colors ${
                  selectedGrade 
                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!selectedGrade}
              >
                🏛️ Atribuie Gradul Masonic
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Change Modal */}
      {showEmailChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">📧 Schimbare Email Admin</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Curent</label>
                <input
                  type="email"
                  value={emailData.current_email}
                  onChange={(e) => setEmailData({...emailData, current_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="email.curent@mlnr1880.ro"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Nou</label>
                <input
                  type="email"
                  value={emailData.new_email}
                  onChange={(e) => setEmailData({...emailData, new_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="email.nou@mlnr1880.ro"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parolă Curentă (pentru confirmare)</label>
                <input
                  type="password"
                  value={emailData.password}
                  onChange={(e) => setEmailData({...emailData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-yellow-800 text-xs">
                <strong>⚠️ Atenție:</strong> După schimbarea email-ului va trebui să vă reconectați cu noul email și parola curentă.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailChange(false);
                  setEmailData({current_email: '', new_email: '', password: ''});
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={changeEmail}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md"
              >
                📧 Schimbă Email-ul
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup & Restore Modal */}
      {showBackupOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">💾 Backup & Restore Sistem MLNR 1880</h3>
            
            <div className="space-y-6">
              {/* Backup Section */}
              <div className="border rounded-lg p-4 bg-orange-50">
                <h4 className="font-semibold mb-3 text-orange-800">📦 Creează Backup</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Backup-ul include toate datele: utilizatori, mesaje, sondaje, notificări, setări, etc.
                </p>
                <button
                  onClick={createBackup}
                  disabled={isCreatingBackup}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                >
                  {isCreatingBackup ? '⏳ Creez backup și descarc fișierul...' : '💾 Creează și Descarcă Backup'}
                </button>
              </div>

              {/* Restore Section */}
              <div className="border rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold mb-3 text-red-800">🔄 Restaurează din Backup</h4>
                <p className="text-sm text-gray-600 mb-3">
                  ⚠️ <strong>ATENȚIE!</strong> Restaurarea va șterge TOATE datele existente și le va înlocui cu cele din backup!
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selectează Fișier Backup (.json)
                    </label>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleBackupFileUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  
                  {backupData && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-800 text-sm">
                        ✅ <strong>Backup încărcat:</strong><br/>
                        Creat la: {backupData.timestamp}<br/>
                        Versiune: {backupData.version}<br/>
                        Utilizatori: {backupData.data?.statistics?.total_users || 0}<br/>
                        Mesaje: {backupData.data?.statistics?.total_messages || 0}
                      </p>
                    </div>
                  )}
                  
                  <button
                    onClick={restoreBackup}
                    disabled={!backupData || isRestoringBackup}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  >
                    {isRestoringBackup ? '🔄 Restaurez date...' : '🔄 RESTAUREAZĂ DATELE (PERICULOS!)'}
                  </button>
                </div>
              </div>

              {/* Warning Section */}
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-400">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Recomandări importante:</strong>
                    </p>
                    <ul className="mt-2 text-xs text-yellow-600 list-disc list-inside">
                      <li>Creați backup-uri regulate (săptămânal)</li>
                      <li>Păstrați backup-urile în locuri sigure</li>
                      <li>Testați backup-urile periodic pe un sistem de test</li>
                      <li>Restaurarea poate dura câteva minute pentru volume mari de date</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBackupOptions(false);
                  setBackupData(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AuthPage = () => {
  const { appSettings } = useAppSettings();
  const [isLogin, setIsLogin] = useState(true);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-blue-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg p-1">
            <img 
              src={appSettings.logo_image ? `data:image/png;base64,${appSettings.logo_image}` : "/mlnr-logo.png"}
              alt="MLNR 1880 - Marea Loja Națională Română"
              className="w-22 h-22 object-contain rounded-full"
            />
          </div>
          <div 
            className="text-white px-6 py-3 rounded-lg shadow-lg mb-4 mx-auto max-w-xs"
            style={{background: appSettings.header_background}}
          >
            <h1 className="text-3xl font-bold mb-2">MLNR 1880</h1>
            <p className="text-sm opacity-90 font-medium">Marea Loja Națională Română</p>
            <p className="text-xs opacity-75">Fondată în 1880</p>
          </div>
        </div>

        {isLogin ? <LoginForm /> : <RegisterForm />}

        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            📜 Despre MLNR 1880 - Marea Loja Națională Română
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <h4 className="font-semibold mb-2 text-blue-800">🏛️ Istoric</h4>
              <p>Fondată în <strong>1880</strong>, MLNR este cea mai veche obediență masonică din România, 
              cu o tradiție neîntreruptă de peste 145 de ani în promovarea valorilor francmasonice.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-purple-800">⚖️ Principii</h4>
              <p>Libertate, Egalitate, Fraternitate - valorile fundamentale care ghidează activitatea noastră 
              în construirea unei societăți mai bune și mai juste.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-green-800">🤝 Misiune</h4>
              <p>Dezvoltarea morală și intelectuală a membrilor, promovarea toleranței și contribuția 
              la progresul umanității prin acțiuni concrete în comunitate.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-red-800">🎯 Viziune</h4>
              <p>Să fim o comunitate unită de frați care lucrează împreună pentru binele comun, 
              păstrând tradițiile și adaptându-ne la provocările vremurilor moderne.</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-blue-200 hover:text-white text-sm font-medium"
          >
            {isLogin ? 'Candidat nou? Solicitați înregistrarea' : 'Membru MLNR 1880? Autentificați-vă aici'}
          </button>
        </div>

        <div className="text-center mt-4">
          <p className="text-blue-200 text-xs">
            Maestru Venerat - Folosiți credențialele oficiale MLNR 1880
          </p>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();
  const { appSettings } = useAppSettings();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  // Prepare background style
  const backgroundStyle = appSettings.background_image 
    ? {
        backgroundImage: `url(data:image/png;base64,${appSettings.background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }
    : {};

  return (
    <div className="min-h-screen bg-gray-100" style={backgroundStyle}>
      {/* Add overlay if background image exists */}
      {appSettings.background_image && (
        <div className="fixed inset-0 bg-black bg-opacity-20 pointer-events-none z-0"></div>
      )}
      <div className="relative z-10">
        <Header />
        <main className="py-8">
          {user.role === 'admin' && <AdminDashboard />}
          {user.role === 'member' && <MemberDashboard />}
          {user.role === 'candidate' && <CandidateDashboard />}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <AppSettingsProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </AppSettingsProvider>
  );
}

export default App;